import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Search, 
  Sparkles, 
  Globe,
  User,
  Target,
  FileText,
  Calendar,
  Clock,
  MapPin,
  Video,
  Percent,
  Truck,
  Settings,
  DollarSign,
  Bot,
  MessageCircle,
  Phone,
  Loader2,
  CheckCircle2,
  Zap,
  Users,
  Award,
  Pencil,
  ArrowRight,
  Shield,
  Gift,
  Trophy,
  Heart,
  AlertTriangle,
  Gem,
  ChevronDown,
  ChevronUp,
  RotateCcw,
  RefreshCw,
  X,
  Plus,
  Save,
  XCircle,
  Palette,
  Image as ImageIcon,
  Upload,
  FileText as LandingIcon,
  Mail,
  Megaphone,
  Link as LinkIcon,
  Play,
  LayoutTemplate
} from 'lucide-react';
import { toast } from 'sonner';
import { getStrategyById, getVariantById } from '@/config/funnelStrategies';
import BrandAssets from '@/components/BrandAssets';
import { FirstTimeHint } from '@/components/onboarding/OnboardingHelpers';
import BriefTemplateGallery from '@/components/onboarding/BriefTemplateGallery';

// ==================== EDITABLE TAGS COMPONENT ====================
const EditableTags = ({ tags, onChange, colorClass, bgClass }) => {
  const [newTag, setNewTag] = useState('');
  const inputRef = useRef(null);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && newTag.trim()) {
      e.preventDefault();
      onChange([...tags, newTag.trim()]);
      setNewTag('');
    }
  };

  const removeTag = (index) => {
    onChange(tags.filter((_, i) => i !== index));
  };

  return (
    <div className="flex flex-wrap gap-2">
      {tags.map((tag) => (
        <span 
          key={tag} 
          className={`text-xs ${bgClass} ${colorClass} px-3 py-1.5 rounded-full flex items-center gap-1.5 group`}
        >
          {tag}
          <button 
            onClick={() => removeTag(tags.indexOf(tag))}
            className="opacity-60 hover:opacity-100 transition-opacity"
          >
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        type="text"
        value={newTag}
        onChange={(e) => setNewTag(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="+ Añadir..."
        className={`text-xs ${colorClass} bg-transparent border border-dashed ${colorClass.replace('text-', 'border-')}/30 px-3 py-1.5 rounded-full w-24 focus:outline-none focus:border-opacity-100 placeholder:opacity-50`}
      />
    </div>
  );
};

// ==================== AUTO-EXPAND TEXTAREA ====================
const AutoExpandTextarea = ({ value, onChange, className, placeholder }) => {
  const textareaRef = useRef(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [value]);

  return (
    <textarea
      ref={textareaRef}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={`resize-none overflow-hidden ${className}`}
      rows={2}
    />
  );
};

// ==================== SKELETON LOADER FOR CARDS ====================
const CardSkeleton = ({ colorClass }) => (
  <div className="p-5 space-y-4 animate-pulse">
    <div className={`h-4 ${colorClass} rounded w-1/3 opacity-20`}></div>
    <div className={`h-3 ${colorClass} rounded w-full opacity-10`}></div>
    <div className={`h-3 ${colorClass} rounded w-3/4 opacity-10`}></div>
    <div className={`h-3 ${colorClass} rounded w-5/6 opacity-10`}></div>
  </div>
);

const NICHES = [
  { id: 'finanzas', name: 'Finanzas e inversión' },
  { id: 'salud', name: 'Salud y bienestar' },
  { id: 'fitness', name: 'Fitness y deporte' },
  { id: 'nutricion', name: 'Nutrición y pérdida de peso' },
  { id: 'negocios', name: 'Negocios y emprendimiento' },
  { id: 'marketing', name: 'Marketing digital' },
  { id: 'desarrollo', name: 'Desarrollo personal' },
  { id: 'relaciones', name: 'Relaciones y coaching' },
  { id: 'idiomas', name: 'Idiomas y formación' },
  { id: 'inmobiliario', name: 'Inmobiliario' },
  { id: 'legal', name: 'Legal y asesoría' },
  { id: 'ecommerce', name: 'Ecommerce y producto físico' },
  { id: 'tech', name: 'Tecnología y SaaS' },
  { id: 'arte', name: 'Arte, música y creatividad' },
  { id: 'coaching', name: 'Coaching y mentoría' },
  { id: 'educacion', name: 'Educación y cursos online' },
  { id: 'otro', name: 'Otro' },
];

const BriefStep = ({
  clients,
  selectedClient,
  onSelectClient,
  funnelName,
  onSetFunnelName,
  strategy,
  variant,
  brief,
  onUpdateBrief,
  funnel,
  onContinueToLandings,
}) => {
  const { api } = useAuth();
  
  // Get strategy details
  const strategyData = getStrategyById(strategy);
  const variantData = getVariantById(strategy, variant);
  
  // ==================== SECTION A: BASE FIELDS ====================
  const [websiteUrl, setWebsiteUrl] = useState(brief?.website_url || '');
  const [niche, setNiche] = useState(brief?.niche || '');
  const [productName, setProductName] = useState(brief?.product_name || '');
  const [price, setPrice] = useState(brief?.price || '');
  const [hasUpsell, setHasUpsell] = useState(brief?.has_upsell || false);
  const [upsellName, setUpsellName] = useState(brief?.upsell_name || '');
  const [upsellPrice, setUpsellPrice] = useState(brief?.upsell_price || '');
  const [whatYouSell, setWhatYouSell] = useState(brief?.what_you_sell || '');
  const [targetAudience, setTargetAudience] = useState(brief?.target_audience || '');
  const [mainPromise, setMainPromise] = useState(brief?.main_promise || '');
  
  // ==================== SECTION B: DYNAMIC FIELDS ====================
  const [eventDate, setEventDate] = useState(brief?.event_date || '');
  const [eventTime, setEventTime] = useState(brief?.event_time || '');
  const [expertName, setExpertName] = useState(brief?.expert_name || '');
  const [platform, setPlatform] = useState(brief?.platform || 'zoom');
  const [discountPercent, setDiscountPercent] = useState(brief?.discount_percent || '');
  const [shippingConditions, setShippingConditions] = useState(brief?.shipping_conditions || '');
  const [coreFeatures, setCoreFeatures] = useState(brief?.core_features || '');
  const [configScope, setConfigScope] = useState(brief?.config_scope || 'global');
  const [physicalAddress, setPhysicalAddress] = useState(brief?.physical_address || '');
  const [serviceRadius, setServiceRadius] = useState(brief?.service_radius || '');
  const [minimumRequirement, setMinimumRequirement] = useState(brief?.minimum_requirement || '');
  const [botPlatform, setBotPlatform] = useState(brief?.bot_platform || 'whatsapp');
  const [botTone, setBotTone] = useState(brief?.bot_tone || 'profesional');
  
  // ==================== SALA URLs (Lanzamiento variants) ====================
  const [salaDirecto1Url, setSalaDirecto1Url] = useState(brief?.sala_directo_1_url || '');
  const [salaDirecto2Url, setSalaDirecto2Url] = useState(brief?.sala_directo_2_url || '');
  const [salaDirecto3Url, setSalaDirecto3Url] = useState(brief?.sala_directo_3_url || '');
  const [salaLive1Url, setSalaLive1Url] = useState(brief?.sala_live_1_url || '');
  const [salaLive2Url, setSalaLive2Url] = useState(brief?.sala_live_2_url || '');
  const [salaLive3Url, setSalaLive3Url] = useState(brief?.sala_live_3_url || '');
  
  // ==================== REPLAY URLs ====================
  const [replayDirecto1Url, setReplayDirecto1Url] = useState(brief?.replay_directo_1_url || '');
  const [replayDirecto2Url, setReplayDirecto2Url] = useState(brief?.replay_directo_2_url || '');
  const [replayDirecto3Url, setReplayDirecto3Url] = useState(brief?.replay_directo_3_url || '');
  
  // ==================== DIRECTO 2 (for 2+ directos variants) ====================
  const [fechaDirecto2, setFechaDirecto2] = useState(brief?.fecha_directo_2 || '');
  const [horaDirecto2, setHoraDirecto2] = useState(brief?.hora_directo_2 || '');
  
  // ==================== URGENCIA Y OFERTA ====================
  const [cuentaAtrasOferta, setCuentaAtrasOferta] = useState(brief?.cuenta_atras_oferta || '48');
  const [diasGarantia, setDiasGarantia] = useState(brief?.dias_garantia || '30');
  const [plazasRestantes, setPlazasRestantes] = useState(brief?.plazas_restantes || '');
  
  // ==================== RECURSOS Y COMUNIDAD ====================
  const [grupoPrivadoUrl, setGrupoPrivadoUrl] = useState(brief?.grupo_privado_url || '');
  const [areaMiembrosUrl, setAreaMiembrosUrl] = useState(brief?.area_miembros_url || '');
  
  // ==================== NEW STRATEGIC FIELDS ====================
  const [mainObjections, setMainObjections] = useState(brief?.main_objections || '');
  const [offerDetails, setOfferDetails] = useState(brief?.offer_details || '');
  const [toneOfVoice, setToneOfVoice] = useState(brief?.tone_of_voice || 'cercano');
  const [painPoints, setPainPoints] = useState(brief?.pain_points || '');
  const [solutionOverview, setSolutionOverview] = useState(brief?.solution_overview || '');
  
  // ==================== ANALYZE WEB CONFIRMATION ====================
  const [showOverwriteConfirm, setShowOverwriteConfirm] = useState(false);
  
  // ==================== VALIDATION STATE ====================
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  
  // Check if required fields are filled
  const isProductNameEmpty = !productName.trim();

  // ==================== URL NORMALIZATION ====================
  const normalizeUrl = (url) => {
    if (!url) return url;
    const trimmed = url.trim();
    if (!trimmed) return trimmed;
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      return trimmed;
    }
    return `https://${trimmed}`;
  };

  const handleWebsiteUrlBlur = () => {
    if (websiteUrl) {
      setWebsiteUrl(normalizeUrl(websiteUrl));
    }
  };
  
  // ==================== GENERATION STATES ====================
  const [analyzingWeb, setAnalyzingWeb] = useState(false);
  const [generatingBrief, setGeneratingBrief] = useState(false);
  const [briefGenerated, setBriefGenerated] = useState(brief?.generated ? true : false);
  const [generatedContent, setGeneratedContent] = useState(brief?.generated || null);
  const [selectedTitle, setSelectedTitle] = useState(brief?.selected_title || '0');
  const [showForm, setShowForm] = useState(!brief?.generated);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  
  // ==================== TEMPLATE GALLERY STATE ====================
  const [showTemplateGallery, setShowTemplateGallery] = useState(() => {
    // Show gallery on first load if brief is empty (no product name)
    const hasExistingBrief = brief?.product_name || brief?.generated;
    const hasSeenGallery = localStorage.getItem('brief_template_gallery_seen');
    return !hasExistingBrief && !hasSeenGallery;
  });

  // Loading messages for brief generation
  const loadingMessages = [
    "✨ Preparando tu funnel personalizado...",
    "🚀 Construyendo tu estrategia de lanzamiento...",
    "🎯 Analizando tu producto para maximizar conversiones...",
    "💡 Diseñando el camino hacia tus primeras ventas...",
    "⚡ Generando copy que convierte...",
    "🔥 Creando tu máquina de ventas..."
  ];

  // Rotate loading messages every 2 seconds
  useEffect(() => {
    if (!generatingBrief) return;
    
    const interval = setInterval(() => {
      setLoadingMessageIndex((prev) => (prev + 1) % loadingMessages.length);
    }, 2000);
    
    return () => {
      clearInterval(interval);
      // Reset message index when effect cleans up (generatingBrief becomes false)
    };
  }, [generatingBrief, loadingMessages.length]);
  
  // Reset loading message index when generation completes
  useEffect(() => {
    if (!generatingBrief) {
      setLoadingMessageIndex(0);
    }
  }, [generatingBrief]);

  // ==================== EDIT MODE STATES ====================
  const [editingCard, setEditingCard] = useState(null); // 'avatar' | 'value' | 'offer' | 'titles' | null
  const [regeneratingCard, setRegeneratingCard] = useState(null);
  
  // Edit buffers for each card
  const [avatarEdit, setAvatarEdit] = useState(null);
  const [valueEdit, setValueEdit] = useState(null);
  const [offerEdit, setOfferEdit] = useState(null);
  const [titlesEdit, setTitlesEdit] = useState(null);

  // ==================== BRAND ASSETS STATE ====================
  const [brandAssets, setBrandAssets] = useState(brief?.brand_assets || {
    expertPhoto: null,
    productPhoto: null,
    iconStyle: 'linear'
  });

  // ==================== BRANDING COLORS & LOGO ====================
  const [primaryColor, setPrimaryColor] = useState(brief?.primary_color || '');
  const [secondaryColor, setSecondaryColor] = useState(brief?.secondary_color || '');
  const [accentColor, setAccentColor] = useState(brief?.accent_color || '');
  const [projectLogo, setProjectLogo] = useState(brief?.project_logo || brief?.logo_url || '');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [extractingBrandColors, setExtractingBrandColors] = useState(false);
  
  // Estilo visual de marca (nuevos campos)
  const [marcaEstiloVisual, setMarcaEstiloVisual] = useState(
    brief?.marca_estilo_visual || 'minimalista, moderno, mucho aire, degradados suaves, tech cercano'
  );
  const [marcaEvitar, setMarcaEvitar] = useState(
    brief?.marca_evitar || 'stock corporativo, personas falsas sonriendo, iconos cliché, saturación excesiva, texto en la imagen'
  );

  // ==================== META PIXEL TRACKING ====================
  const [metaPixelId, setMetaPixelId] = useState(brief?.meta_pixel_id || '');

  // ==================== AUTO-SAVE STATE ====================
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const saveTimeoutRef = useRef(null);

  // ==================== SYNC BRIEF PROP WITH LOCAL STATE ====================
  // This ensures that when brief prop changes (e.g., from server load), local state updates
  useEffect(() => {
    if (brief) {
      // Only update if brief has actual data and local state is empty
      if (brief.product_name && !productName) setProductName(brief.product_name);
      if (brief.price && !price) setPrice(brief.price);
      if (brief.what_you_sell && !whatYouSell) setWhatYouSell(brief.what_you_sell);
      if (brief.target_audience && !targetAudience) setTargetAudience(brief.target_audience);
      if (brief.main_promise && !mainPromise) setMainPromise(brief.main_promise);
      if (brief.expert_name && !expertName) setExpertName(brief.expert_name);
      if (brief.niche && !niche) setNiche(brief.niche);
      if (brief.website_url && !websiteUrl) setWebsiteUrl(brief.website_url);
      
      // Update generated content if it exists
      if (brief.generated && !generatedContent) {
        setGeneratedContent(brief.generated);
        setBriefGenerated(true);
        setShowForm(false);
      }
    }
  }, [brief]);

  // Check if this strategy requires event fields
  const needsEventFields = ['lanzamiento', 'plf', 'eventos', 'desafio'].includes(strategy);
  
  // Form validation - require product_name, and expert_name ONLY if strategy needs event fields
  const isFormValid = productName.trim().length > 0 && (needsEventFields ? expertName.trim().length > 0 : true);
  const isExpertNameEmpty = !expertName.trim();

  // ==================== AUTO-SAVE FUNCTION ====================
  const saveBriefToDatabase = useCallback(async (briefData) => {
    if (!funnel?.id) return;
    
    setIsSaving(true);
    try {
      await api.put(`/funnels/${funnel.id}`, { brief: briefData });
      setLastSaved(new Date());
      
      // DISABLED: Auto cascade generation was confusing users
      // Content will only be generated when user explicitly clicks "Generate" in each step
      // const hasMinimumData = briefData.productName || briefData.product_name;
      // const hasAudience = briefData.targetAudience || briefData.target_audience;
      // if (hasMinimumData && hasAudience) {
      //   triggerCascadeGeneration();
      // }
    } catch (error) {
      // Silent fail for auto-save - don't disrupt user
    } finally {
      setIsSaving(false);
    }
  }, [api, funnel?.id]);

  // ==================== CASCADE AUTO-DRAFT GENERATION ====================
  const [cascadeTriggered, setCascadeTriggered] = useState(false);
  const [cascadeStatus, setCascadeStatus] = useState(null);
  const [showCascadeProgress, setShowCascadeProgress] = useState(false);
  const cascadePollingRef = useRef(null);
  
  const triggerCascadeGeneration = useCallback(async () => {
    // Only trigger once per session to avoid multiple generations
    if (cascadeTriggered || !funnel?.id) return;
    
    try {
      setCascadeTriggered(true);
      setShowCascadeProgress(true);
      
      await api.post('/ai/cascade-auto-draft', {
        funnel_id: funnel.id,
        generate_landings: true,
        generate_emails: true,
        generate_ads: true
      });
      
      toast.success('Generando borradores automáticamente', {
        description: 'Los borradores de Landings, Emails y Ads estarán listos en unos segundos',
        duration: 5000
      });
      
      // Start polling for status
      startCascadePolling();
    } catch (error) {
      setShowCascadeProgress(false);
      // Don't show error to user - it's a background enhancement
    }
  }, [api, funnel?.id, cascadeTriggered]);
  
  // Poll for cascade generation status
  const startCascadePolling = useCallback(() => {
    if (cascadePollingRef.current) {
      clearInterval(cascadePollingRef.current);
    }
    
    // Set a timeout to stop polling after 60 seconds
    const maxPollingTime = 60000;
    const startTime = Date.now();
    
    cascadePollingRef.current = setInterval(async () => {
      // Check if we've exceeded the max polling time
      if (Date.now() - startTime > maxPollingTime) {
        clearInterval(cascadePollingRef.current);
        cascadePollingRef.current = null;
        setCascadeStatus(prev => ({ ...prev, status: 'completed' }));
        setTimeout(() => setShowCascadeProgress(false), 1000);
        return;
      }
      
      try {
        const response = await api.get(`/ai/cascade-status/${funnel?.id}`);
        setCascadeStatus(response.data);
        
        if (response.data.status === 'completed' || response.data.status === 'error') {
          clearInterval(cascadePollingRef.current);
          cascadePollingRef.current = null;
          
          if (response.data.status === 'completed') {
            toast.success('Borradores generados', {
              description: 'Los borradores de Landings, Emails y Ads están listos'
            });
          } else {
            toast.error('Error en la generación', {
              description: response.data.errors?.join(', ') || 'Error desconocido'
            });
          }
          
          // Hide progress after a short delay
          setTimeout(() => setShowCascadeProgress(false), 3000);
        }
      } catch (error) {
        // Silent fail for polling - but stop after too many failures
        clearInterval(cascadePollingRef.current);
        cascadePollingRef.current = null;
        setTimeout(() => setShowCascadeProgress(false), 1000);
      }
    }, 2000);
  }, [api, funnel?.id]);
  
  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (cascadePollingRef.current) {
        clearInterval(cascadePollingRef.current);
      }
    };
  }, []);

  // Debounced auto-save (saves 1.5 seconds after last change)
  const debouncedSave = useCallback((briefData) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      saveBriefToDatabase(briefData);
    }, 1500);
  }, [saveBriefToDatabase]);

  // Update parent brief when fields change AND trigger auto-save
  useEffect(() => {
    const briefData = {
      website_url: websiteUrl,
      niche,
      product_name: productName,
      productName: productName, // Alias for compatibility
      price,
      has_upsell: hasUpsell,
      upsell_name: upsellName,
      upsell_price: upsellPrice,
      what_you_sell: whatYouSell,
      target_audience: targetAudience,
      targetAudience: targetAudience, // Alias
      main_promise: mainPromise,
      mainPromise: mainPromise, // Alias
      // NEW STRATEGIC FIELDS
      pain_points: painPoints,
      painPoints: painPoints,
      solution_overview: solutionOverview,
      solutionOverview: solutionOverview,
      main_objections: mainObjections,
      mainObjections: mainObjections,
      offer_details: offerDetails,
      offerDetails: offerDetails,
      tone_of_voice: toneOfVoice,
      toneOfVoice: toneOfVoice,
      // END NEW FIELDS
      event_date: eventDate,
      expert_name: expertName,
      expertName: expertName, // Alias
      event_time: eventTime,
      eventTime: eventTime, // Alias
      platform,
      discount_percent: discountPercent,
      shipping_conditions: shippingConditions,
      core_features: coreFeatures,
      config_scope: configScope,
      physical_address: physicalAddress,
      service_radius: serviceRadius,
      minimum_requirement: minimumRequirement,
      bot_platform: botPlatform,
      bot_tone: botTone,
      generated: generatedContent,
      selected_title: selectedTitle,
      brand_assets: brandAssets,
      // Branding colors (sin valores por defecto hardcodeados)
      primary_color: primaryColor || null,
      primaryColor: primaryColor || null,
      secondary_color: secondaryColor || null,
      secondaryColor: secondaryColor || null,
      accent_color: accentColor || null,
      accentColor: accentColor || null,
      project_logo: projectLogo || null,
      projectLogo: projectLogo || null,
      logo_url: projectLogo || null,
      // Estilo visual de marca
      marca_estilo_visual: marcaEstiloVisual,
      marca_evitar: marcaEvitar,
      // NEW: Sala URLs for Lanzamiento variants
      sala_directo_1_url: salaDirecto1Url,
      sala_directo_2_url: salaDirecto2Url,
      sala_directo_3_url: salaDirecto3Url,
      sala_live_1_url: salaLive1Url,
      sala_live_2_url: salaLive2Url,
      sala_live_3_url: salaLive3Url,
      // NEW: Replay URLs
      replay_directo_1_url: replayDirecto1Url,
      replay_directo_2_url: replayDirecto2Url,
      replay_directo_3_url: replayDirecto3Url,
      // NEW: Directo 2 dates (for 2+ directos)
      fecha_directo_2: fechaDirecto2,
      hora_directo_2: horaDirecto2,
      // NEW: Urgencia y oferta
      cuenta_atras_oferta: cuentaAtrasOferta,
      dias_garantia: diasGarantia,
      plazas_restantes: plazasRestantes,
      // NEW: Recursos y comunidad
      grupo_privado_url: grupoPrivadoUrl,
      area_miembros_url: areaMiembrosUrl,
      // META PIXEL: ID para tracking de conversiones
      meta_pixel_id: metaPixelId || null,
    };
    
    // Update parent state
    onUpdateBrief(briefData);
    
    // Trigger debounced auto-save to database
    if (funnel?.id && (productName || niche)) {
      debouncedSave(briefData);
    }
  }, [
    websiteUrl, niche, productName, price, hasUpsell, upsellName, upsellPrice,
    whatYouSell, targetAudience, mainPromise, eventDate, eventTime, expertName, platform,
    discountPercent, shippingConditions, coreFeatures, configScope,
    physicalAddress, serviceRadius, minimumRequirement, botPlatform, botTone,
    generatedContent, selectedTitle, brandAssets,
    primaryColor, secondaryColor, accentColor, projectLogo, marcaEstiloVisual, marcaEvitar,
    painPoints, solutionOverview, mainObjections, offerDetails, toneOfVoice,
    salaDirecto1Url, salaDirecto2Url, salaDirecto3Url, salaLive1Url, salaLive2Url, salaLive3Url,
    replayDirecto1Url, replayDirecto2Url, replayDirecto3Url,
    fechaDirecto2, horaDirecto2, cuentaAtrasOferta, diasGarantia, plazasRestantes,
    grupoPrivadoUrl, areaMiembrosUrl, metaPixelId,
    debouncedSave, funnel?.id, onUpdateBrief
  ]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Check if form has existing data
  const hasExistingData = () => {
    return productName || whatYouSell || targetAudience || mainPromise || 
           mainObjections || offerDetails || painPoints || solutionOverview;
  };

  // Analyze website with Scraping + LLM
  const analyzeWebsite = async () => {
    if (!websiteUrl) {
      toast.error('Introduce una URL válida');
      return;
    }
    
    // Check if there's existing data that would be overwritten
    if (hasExistingData()) {
      setShowOverwriteConfirm(true);
      return;
    }
    
    executeWebAnalysis();
  };
  
  const executeWebAnalysis = async () => {
    setShowOverwriteConfirm(false);
    setAnalyzingWeb(true);
    
    try {
      toast.info('Analizando web y redactando briefing...', {
        description: 'Esto puede tardar unos segundos',
        duration: 10000
      });
      
      // Normalize URL - add https:// if no protocol specified
      const normalizedUrl = normalizeUrl(websiteUrl);
      
      const response = await api.post('/ai/analyze-web-smart', { 
        url: normalizedUrl,
        extract_fields: [
          'product_name',
          'what_you_sell',
          'target_audience',
          'main_promise',
          'pain_points',
          'solution_overview',
          'main_objections',
          'offer_details',
          'price',
          'tone_of_voice',
          'niche'
        ]
      });
      
      const analysis = response.data;
      
      if (analysis.success && analysis.extracted) {
        const data = analysis.extracted;
        
        // Helper function to map niche to valid dropdown value
        const mapNiche = (rawNiche) => {
          if (!rawNiche) return null;
          const n = rawNiche.toLowerCase();
          // Direct matches
          const validNiches = ['finanzas', 'salud', 'fitness', 'nutricion', 'negocios', 'marketing', 'desarrollo', 'relaciones', 'idiomas', 'inmobiliario', 'legal', 'ecommerce', 'tech', 'arte', 'coaching', 'educacion', 'otro'];
          if (validNiches.includes(n)) return n;
          // Fuzzy matches
          if (n.includes('finanz') || n.includes('inversion') || n.includes('dinero')) return 'finanzas';
          if (n.includes('salud') || n.includes('bienestar') || n.includes('medic')) return 'salud';
          if (n.includes('fitness') || n.includes('deport') || n.includes('gym')) return 'fitness';
          if (n.includes('nutri') || n.includes('diet') || n.includes('peso')) return 'nutricion';
          if (n.includes('negocio') || n.includes('emprend') || n.includes('empresa')) return 'negocios';
          if (n.includes('marketing') || n.includes('publicidad') || n.includes('ads') || n.includes('agencia')) return 'marketing';
          if (n.includes('desarrollo') || n.includes('personal') || n.includes('crecimiento')) return 'desarrollo';
          if (n.includes('relacion') || n.includes('pareja')) return 'relaciones';
          if (n.includes('idioma') || n.includes('ingles') || n.includes('formacion')) return 'idiomas';
          if (n.includes('inmobil') || n.includes('casa') || n.includes('propiedad')) return 'inmobiliario';
          if (n.includes('legal') || n.includes('abogad') || n.includes('asesoria')) return 'legal';
          if (n.includes('ecommerce') || n.includes('tienda') || n.includes('producto')) return 'ecommerce';
          if (n.includes('tech') || n.includes('software') || n.includes('saas') || n.includes('tecnolog')) return 'tech';
          if (n.includes('arte') || n.includes('music') || n.includes('creativ')) return 'arte';
          if (n.includes('coaching') || n.includes('mentor') || n.includes('coach')) return 'coaching';
          if (n.includes('educacion') || n.includes('curso') || n.includes('aprend')) return 'educacion';
          return 'otro';
        };
        
        // Helper function to map tone_of_voice to valid dropdown value
        const mapTone = (rawTone) => {
          if (!rawTone) return null;
          const t = rawTone.toLowerCase();
          const validTones = ['cercano', 'formal', 'directo', 'urgente', 'inspiracional', 'irreverente'];
          if (validTones.includes(t)) return t;
          // Fuzzy matches
          if (t.includes('cercan') || t.includes('amig') || t.includes('calid')) return 'cercano';
          if (t.includes('formal') || t.includes('profesional') || t.includes('serio')) return 'formal';
          if (t.includes('directo') || t.includes('claro') || t.includes('simple')) return 'directo';
          if (t.includes('urgent') || t.includes('escasez') || t.includes('rapido')) return 'urgente';
          if (t.includes('inspir') || t.includes('motiv') || t.includes('positiv')) return 'inspiracional';
          if (t.includes('irreverent') || t.includes('provocad') || t.includes('atrevid')) return 'irreverente';
          return null;
        };
        
        // Auto-populate fields with fallback for Spanish keys
        const productVal = data.product_name || data.nombre_producto;
        const nicheVal = mapNiche(data.niche || data.nicho);
        const priceVal = data.price || data.precio;
        const whatYouSellVal = data.what_you_sell || data.que_vendes;
        const targetVal = data.target_audience || data.publico_objetivo;
        const promiseVal = data.main_promise || data.promesa || data.promised_result;
        const painVal = data.pain_points || data.dolores;
        const solutionVal = data.solution_overview || data.solucion;
        const objectionsVal = data.main_objections || data.objeciones;
        const offerVal = data.offer_details || data.oferta;
        const toneVal = mapTone(data.tone_of_voice || data.tono);
        
        // Apply values only if they exist (preserve existing values otherwise)
        if (productVal) setProductName(productVal);
        if (nicheVal) setNiche(nicheVal);
        if (priceVal) setPrice(priceVal);
        if (whatYouSellVal) setWhatYouSell(whatYouSellVal);
        if (targetVal) setTargetAudience(targetVal);
        if (promiseVal) setMainPromise(promiseVal);
        if (painVal) setPainPoints(painVal);
        if (solutionVal) setSolutionOverview(solutionVal);
        if (objectionsVal) setMainObjections(objectionsVal);
        if (offerVal) setOfferDetails(offerVal);
        if (toneVal) setToneOfVoice(toneVal);
        
        toast.success('Brief completado automáticamente', {
          description: 'Revisa los campos y ajusta lo que necesites antes de guardar.'
        });
        
        // AUTO-DETECT BRAND COLORS after web analysis
        try {
          const normalizedUrl = normalizeUrl(websiteUrl);
          const brandResponse = await api.post('/ai/extract-brand-colors', { url: normalizedUrl });
          
          if (brandResponse.data?.success && brandResponse.data?.colors) {
            const { colors } = brandResponse.data;
            
            // Proponer colores detectados (si hay alguno)
            if (colors.primary_color) {
              setPrimaryColor(colors.primary_color);
              toast.info(`Color primario detectado: ${colors.primary_color}`, { duration: 3000 });
            }
            if (colors.secondary_color) {
              setSecondaryColor(colors.secondary_color);
            }
            if (colors.accent_color) {
              setAccentColor(colors.accent_color);
            }
            if (colors.logo_url) {
              setProjectLogo(colors.logo_url);
              toast.info('Logo detectado en la web', { duration: 3000 });
            }
          }
        } catch (brandError) {
          // Silent fail - color detection is optional
          console.log('Brand color detection skipped:', brandError.message);
        }
      } else {
        throw new Error(analysis.error || 'No se pudo extraer información');
      }
    } catch (error) {
      toast.error('Error al analizar la web', {
        description: error.response?.data?.detail || error.message || 'Intenta con otra URL'
      });
    } finally {
      setAnalyzingWeb(false);
    }
  };
  
  // Extract brand colors manually (button trigger)
  const extractBrandColors = async () => {
    if (!websiteUrl) {
      toast.error('Introduce una URL primero');
      return;
    }
    
    setExtractingBrandColors(true);
    
    try {
      const normalizedUrl = normalizeUrl(websiteUrl);
      const response = await api.post('/ai/extract-brand-colors', { url: normalizedUrl });
      
      if (response.data?.success && response.data?.colors) {
        const { colors } = response.data;
        
        if (colors.primary_color) setPrimaryColor(colors.primary_color);
        if (colors.secondary_color) setSecondaryColor(colors.secondary_color);
        if (colors.accent_color) setAccentColor(colors.accent_color);
        if (colors.logo_url) setProjectLogo(colors.logo_url);
        
        if (colors.detected_colors?.length > 0) {
          toast.success(`Se detectaron ${colors.detected_colors.length} colores`, {
            description: colors.detected_colors.slice(0, 3).join(', ')
          });
        } else {
          toast.warning('No se detectaron colores automáticamente', {
            description: 'Puedes introducirlos manualmente'
          });
        }
      }
    } catch (error) {
      toast.error('No se pudieron extraer los colores', {
        description: 'Introdúcelos manualmente'
      });
    } finally {
      setExtractingBrandColors(false);
    }
  };

  // Generate brief with AI
  const generateBriefWithAI = async () => {
    if (!isFormValid) {
      toast.error('Completa los campos obligatorios primero');
      return;
    }

    setGeneratingBrief(true);

    // Simulate API call with 2 second delay
    await new Promise(resolve => setTimeout(resolve, 2000));

    try {
      const response = await api.post('/ai/generate-smart-brief', {
        funnel_id: funnel?.id,
        strategy: strategy,
        variant: variant,
        brief_data: {
          niche,
          product_name: productName,
          price,
          what_you_sell: whatYouSell,
          target_audience: targetAudience,
          main_promise: mainPromise,
          expert_name: expertName,
        }
      });
      setGeneratedContent(response.data);
    } catch (error) {
      // Generate realistic mock data based on form inputs
      const mockContent = generateMockBrief();
      setGeneratedContent(mockContent);
    }

    setBriefGenerated(true);
    setShowForm(false);
    setGeneratingBrief(false);
    toast.success('¡Brief de marketing generado con éxito!');
  };

  // Generate realistic mock brief data
  const generateMockBrief = () => {
    const audienceFirstWord = targetAudience.split(' ')[0] || 'profesionales';
    const productFirstWord = productName.split(' ')[0] || 'Programa';
    const nicheNorm = (niche || '').toLowerCase();
    
    // PRIORITY: Use actual pain points from brief if available
    let fears;
    if (painPoints && painPoints.trim().length > 10) {
      // Split pain points by common separators and clean them
      const painPointsList = painPoints
        .split(/[,;\n]+/)
        .map(p => p.trim())
        .filter(p => p.length > 5)
        .slice(0, 3);
      
      if (painPointsList.length >= 2) {
        // Use the actual pain points as fears
        fears = painPointsList.length >= 3 
          ? painPointsList 
          : [...painPointsList, `No lograr ${mainPromise || 'los resultados esperados'}`];
      }
    }
    
    // Fallback: Dynamic fears based on niche/industry (only if no pain points)
    if (!fears) {
      const fearsByNiche = {
        // Marketing & Business
        'marketing': ['Gastar dinero en campañas que no convierten', 'No entender las métricas y tomar malas decisiones', 'Que la competencia me adelante en digital'],
        'negocios': ['Invertir sin ver ROI', 'No escalar correctamente y estancarse', 'Depender de terceros que no entienden mi visión'],
        'emprendimiento': ['Fracasar y perder la inversión inicial', 'No encontrar clientes suficientes', 'Quedarse sin liquidez antes de despegar'],
        'ventas': ['No cerrar suficientes ventas', 'Que los clientes digan que es muy caro', 'Perder clientes frente a competidores más agresivos'],
        
        // Health & Wellness
        'salud': ['Empeorar mi condición por seguir malos consejos', 'Perder tiempo con métodos que no funcionan', 'No ver resultados y desmotivarme'],
        'fitness': ['Lesionarme por entrenar mal', 'No ver cambios físicos a pesar del esfuerzo', 'Perder motivación a mitad de camino'],
        'nutrición': ['Hacer dietas extremas que dañen mi salud', 'Efecto rebote después de tanto sacrificio', 'No poder mantener los resultados a largo plazo'],
        'bienestar': ['Invertir en algo que no me ayude realmente', 'No tener tiempo para cuidarme', 'Sentirme peor intentando mejorar'],
        
        // Education & Professional Development
        'educación': ['Invertir en formación que no aplique', 'Quedarme desactualizado rápidamente', 'No conseguir el trabajo que quiero'],
        'tecnología': ['Quedarme obsoleto profesionalmente', 'No entender las nuevas herramientas', 'Que la IA me quite el trabajo'],
        'finanzas': ['Perder mis ahorros por malas inversiones', 'No entender dónde poner mi dinero', 'Llegar a la jubilación sin suficiente'],
        
        // Creative & Lifestyle
        'creatividad': ['Que mi trabajo no sea valorado', 'No encontrar mi estilo propio', 'Quedarme bloqueado sin inspiración'],
        'productividad': ['Seguir perdiendo tiempo en lo que no importa', 'No cumplir con mis compromisos', 'Sentirme abrumado constantemente'],
        
        // Relationships
        'relaciones': ['Repetir los mismos errores del pasado', 'No encontrar una conexión real', 'Sentirme solo/a a pesar de intentarlo'],
        'coaching': ['Pagar por sesiones que no me transforman', 'Depender emocionalmente del coach', 'No aplicar lo aprendido en mi vida real'],
        
        // Default for unmatched niches
        'default': [`No ver resultados en ${niche || 'mi área'}`, 'Invertir tiempo y dinero sin retorno', 'Quedarme igual que antes']
      };
      
      // Find matching fears
      fears = fearsByNiche.default;
      for (const [key, value] of Object.entries(fearsByNiche)) {
        if (nicheNorm.includes(key) || key.includes(nicheNorm.slice(0, 5))) {
          fears = value;
          break;
        }
      }
    }
    
    // Generate desires based on mainPromise and niche
    const desires = [
      mainPromise || 'Alcanzar mis objetivos más rápido',
      `Tener un sistema probado para ${nicheNorm || 'lograr resultados'}`,
      `Sentir confianza y seguridad en ${targetAudience ? `mi rol como ${audienceFirstWord}` : 'mis decisiones'}`
    ];
    
    return {
      avatar: {
        name: 'Cliente Ideal',
        age: 38,
        occupation: `${audienceFirstWord} que busca resultados`,
        fears,
        desires
      },
      valueProposition: {
        differential: `A diferencia de otros ${niche || 'programas'} genéricos, ${productName} ofrece un sistema paso a paso personalizado con soporte continuo y resultados medibles desde la primera semana.`,
        salesAngle: `El único método que combina estrategia + implementación + comunidad para que ${audienceFirstWord} consigan ${mainPromise || 'resultados reales'} sin perder tiempo en teoría.`
      },
      offer: {
        guarantee: `Garantía de satisfacción de 30 días: Si no ves resultados aplicando el método, te devolvemos el 100% de tu inversión sin preguntas.`,
        bonuses: [
          {
            name: 'Acceso a Comunidad VIP',
            value: '497€',
            description: 'Grupo privado con sesiones Q&A semanales y networking'
          },
          {
            name: 'Plantillas y Recursos Premium',
            value: '297€',
            description: 'Todos los templates, scripts y checklists listos para usar'
          }
        ]
      },
      titles: [
        `Descubre cómo ${mainPromise || 'transformar tu negocio'} en solo 90 días (sin experiencia previa)`,
        `${productFirstWord}: El sistema probado que está ayudando a ${audienceFirstWord} a conseguir resultados extraordinarios`,
        `De cero a experto: La guía definitiva para ${audienceFirstWord} que quieren ${mainPromise || 'dar el siguiente paso'}`
      ]
    };
  };

  // ==================== CARD EDIT FUNCTIONS ====================
  const startEditing = (cardType) => {
    if (cardType === 'avatar') {
      setAvatarEdit({ ...generatedContent.avatar });
    } else if (cardType === 'value') {
      setValueEdit({ ...generatedContent.valueProposition });
    } else if (cardType === 'offer') {
      setOfferEdit({ 
        ...generatedContent.offer,
        bonuses: generatedContent.offer.bonuses.map(b => ({ ...b }))
      });
    } else if (cardType === 'titles') {
      setTitlesEdit([...generatedContent.titles]);
    }
    setEditingCard(cardType);
  };

  const cancelEditing = () => {
    setEditingCard(null);
    setAvatarEdit(null);
    setValueEdit(null);
    setOfferEdit(null);
    setTitlesEdit(null);
  };

  const saveEditing = (cardType) => {
    const newContent = { ...generatedContent };
    
    if (cardType === 'avatar') {
      newContent.avatar = avatarEdit;
    } else if (cardType === 'value') {
      newContent.valueProposition = valueEdit;
    } else if (cardType === 'offer') {
      newContent.offer = offerEdit;
    } else if (cardType === 'titles') {
      newContent.titles = titlesEdit;
    }
    
    setGeneratedContent(newContent);
    setEditingCard(null);
    toast.success('Cambios guardados');
  };

  // ==================== REGENERATION FUNCTIONS ====================
  const regenerateCard = async (cardType) => {
    setRegeneratingCard(cardType);
    
    // Simulate AI regeneration with 1.5s delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    const newContent = { ...generatedContent };
    const audienceFirstWord = targetAudience.split(' ')[0] || 'profesionales';
    const productFirstWord = productName.split(' ')[0] || 'Programa';
    
    if (cardType === 'avatar') {
      const names = ['Carlos Ruiz', 'Ana Martínez', 'Diego Fernández', 'Laura García'];
      const ages = [32, 45, 28, 41, 35, 52];
      const nicheNorm = (niche || '').toLowerCase();
      
      // Dynamic fears based on niche
      const fearsByNiche = {
        'marketing': ['Gastar en campañas que no convierten', 'Que la competencia me adelante en digital', 'No entender las métricas'],
        'negocios': ['Invertir sin ver ROI', 'No escalar correctamente', 'Depender de otros'],
        'emprendimiento': ['Fracasar y perder la inversión', 'No encontrar clientes', 'Quedarse sin liquidez'],
        'ventas': ['No cerrar suficientes ventas', 'Que digan que es muy caro', 'Perder clientes'],
        'salud': ['Empeorar por malos consejos', 'No ver resultados', 'Desmotivarme'],
        'fitness': ['Lesionarme entrenando', 'No ver cambios físicos', 'Perder motivación'],
        'finanzas': ['Perder mis ahorros', 'No entender las inversiones', 'Llegar sin dinero a la jubilación'],
        'tecnología': ['Quedarme obsoleto', 'No entender las herramientas nuevas', 'Que la IA me quite el trabajo'],
        'default': ['Invertir tiempo y no ver resultados', 'Que la competencia me adelante', 'No saber por dónde empezar']
      };
      
      let fears = fearsByNiche.default;
      for (const [key, value] of Object.entries(fearsByNiche)) {
        if (nicheNorm.includes(key)) {
          fears = value;
          break;
        }
      }
      
      newContent.avatar = {
        name: names[Math.floor(Math.random() * names.length)],
        age: ages[Math.floor(Math.random() * ages.length)],
        occupation: `${audienceFirstWord} ambicioso/a`,
        fears,
        desires: [
          'Conseguir libertad financiera',
          'Tener un negocio que funcione solo',
          mainPromise || 'Vivir de lo que me apasiona'
        ]
      };
    } else if (cardType === 'value') {
      newContent.valueProposition = {
        differential: `${productName} no es un curso más: es un sistema de implementación guiada con mentorías semanales y acceso directo al equipo de expertos.`,
        salesAngle: `Para ${audienceFirstWord} que están cansados de teoría sin resultados: el método que te lleva de la mano hasta ${mainPromise || 'conseguir tus objetivos'}.`
      };
    } else if (cardType === 'offer') {
      newContent.offer = {
        guarantee: `Prueba ${productName} durante 14 días sin riesgo. Si no empiezas a ver resultados, te devolvemos cada céntimo.`,
        bonuses: [
          {
            name: 'Masterclass Exclusiva',
            value: '397€',
            description: 'Sesión privada de estrategia personalizada'
          },
          {
            name: 'Grupo de Accountability',
            value: '197€',
            description: 'Acompañamiento semanal para mantener el foco'
          }
        ]
      };
    }
    
    setGeneratedContent(newContent);
    setRegeneratingCard(null);
    toast.success('Contenido regenerado');
  };

  // ==================== TITLE SPECIFIC FUNCTIONS ====================
  const generateNewTitles = async () => {
    setRegeneratingCard('titles');
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    const audienceFirstWord = targetAudience.split(' ')[0] || 'profesionales';
    const newTitles = [
      `El secreto que los ${audienceFirstWord} exitosos no quieren que sepas`,
      `Cómo logré ${mainPromise || 'resultados increíbles'} y tú también puedes hacerlo`,
      `La verdad sobre ${productName}: lo que nadie te ha contado`
    ];
    
    setGeneratedContent({ ...generatedContent, titles: newTitles });
    setTitlesEdit(null);
    setRegeneratingCard(null);
    toast.success('Nuevos títulos generados');
  };

  const generateVariations = async (baseIndex) => {
    setRegeneratingCard('titles-variation');
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    const baseTitle = generatedContent.titles[baseIndex];
    const words = baseTitle.split(' ');
    const keyword = words.slice(0, 3).join(' ');
    
    const variations = generatedContent.titles.map((title, i) => {
      if (i === baseIndex) return title;
      // Generate variations based on the selected title
      const variationOptions = [
        `${keyword}: la guía definitiva para ${new Date().getFullYear()}`,
        `${keyword} (método paso a paso revelado)`,
        `NUEVO: ${keyword} - resultados garantizados`
      ];
      return variationOptions[i % variationOptions.length];
    });
    
    setGeneratedContent({ ...generatedContent, titles: variations });
    setRegeneratingCard(null);
    toast.success('Variaciones creadas');
  };

  // ==================== BONUS MANAGEMENT ====================
  const addBonus = () => {
    setOfferEdit({
      ...offerEdit,
      bonuses: [
        ...offerEdit.bonuses,
        { name: '', value: '', description: '' }
      ]
    });
  };

  const removeBonus = (index) => {
    setOfferEdit({
      ...offerEdit,
      bonuses: offerEdit.bonuses.filter((_, i) => i !== index)
    });
  };

  const updateBonus = (index, field, value) => {
    const newBonuses = [...offerEdit.bonuses];
    newBonuses[index] = { ...newBonuses[index], [field]: value };
    setOfferEdit({ ...offerEdit, bonuses: newBonuses });
  };

  // Reset and edit form
  const resetToEdit = () => {
    setShowForm(true);
    setBriefGenerated(false);
  };

  // Check if strategy needs specific fields (needsEventFields is defined earlier for validation)
  const needsEcommerceFields = strategy === 'ecommerce';
  const needsSaaSFields = strategy === 'saas';
  const needsLocalFields = strategy === 'local';
  const needsHighTicketFields = strategy === 'high-ticket';
  const needsIAFields = strategy === 'agentes-ia';
  
  // Validation helper - only require expertName for event strategies
  const hasValidationErrors = isProductNameEmpty || (needsEventFields && isExpertNameEmpty);

  // ==================== LOADING STATE ====================
  if (generatingBrief) {
    return (
      <div data-testid="brief-step-loading" className="flex flex-col items-center justify-center min-h-[500px] space-y-6">
        <div className="relative">
          <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-brand to-purple-600 flex items-center justify-center animate-pulse">
            <Sparkles className="w-12 h-12 text-white" />
          </div>
          <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-[#00D68F] flex items-center justify-center animate-bounce">
            <Loader2 className="w-4 h-4 text-white animate-spin" />
          </div>
        </div>
        
        <div className="text-center space-y-2">
          <h3 className="text-xl font-bold text-text-main transition-all duration-500">
            {loadingMessages[loadingMessageIndex]}
          </h3>
          <p className="text-text-secondary max-w-md">
            Creando avatar, propuesta de valor, oferta irresistible y títulos persuasivos...
          </p>
        </div>

        {/* Loading skeleton cards */}
        <div className="grid grid-cols-2 gap-4 w-full max-w-2xl mt-8">
          {[1, 2, 3, 4].map((num) => (
            <div key={`skeleton-${num}`} className="bg-surface border border-border-subtle rounded-xl p-4 space-y-3 animate-pulse">
              <div className="h-4 bg-app-bg rounded w-1/2"></div>
              <div className="h-3 bg-app-bg rounded w-full"></div>
              <div className="h-3 bg-app-bg rounded w-3/4"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ==================== RESULTS VIEW ====================
  if (briefGenerated && generatedContent && !showForm) {
    return (
      <div data-testid="brief-step-results" className="space-y-6">
        {/* Cascade Progress Indicator */}
        {showCascadeProgress && cascadeStatus && (
          <div 
            className="fixed bottom-6 right-6 z-50 bg-surface border border-brand/30 rounded-2xl shadow-xl p-4 w-80"
            data-testid="cascade-progress"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-brand/20 flex items-center justify-center">
                {cascadeStatus.status === 'completed' ? (
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                ) : cascadeStatus.status === 'error' ? (
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                ) : (
                  <Loader2 className="w-5 h-5 text-brand-500 animate-spin" />
                )}
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-text-main">
                  {cascadeStatus.status === 'completed' 
                    ? 'Borradores listos' 
                    : cascadeStatus.status === 'error'
                      ? 'Error en generación'
                      : 'Generando borradores...'}
                </p>
                <p className="text-xs text-text-muted">
                  {cascadeStatus.status === 'in_progress' && cascadeStatus.current_step === 'landings' && 'Creando Landing Page...'}
                  {cascadeStatus.status === 'in_progress' && cascadeStatus.current_step === 'emails' && 'Creando Secuencia de Emails...'}
                  {cascadeStatus.status === 'in_progress' && cascadeStatus.current_step === 'ads' && 'Creando Copy de Ads...'}
                  {cascadeStatus.status === 'completed' && 'Todo listo para los siguientes pasos'}
                  {cascadeStatus.status === 'error' && cascadeStatus.errors?.[0]}
                </p>
              </div>
              <button 
                onClick={() => setShowCascadeProgress(false)}
                className="p-1 hover:bg-app-bg rounded-lg transition-colors"
              >
                <X className="w-4 h-4 text-text-muted" />
              </button>
            </div>
            
            {/* Progress Bar */}
            <div className="space-y-2">
              <div className="h-1.5 bg-app-bg rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all duration-500 ${
                    cascadeStatus.status === 'completed' 
                      ? 'bg-green-400' 
                      : cascadeStatus.status === 'error'
                        ? 'bg-red-400'
                        : 'bg-brand'
                  }`}
                  style={{ width: `${cascadeStatus.progress_percent}%` }}
                />
              </div>
              
              {/* Step indicators */}
              <div className="flex items-center justify-between text-[10px]">
                <div className={`flex items-center gap-1 ${cascadeStatus.drafts_generated?.landings ? 'text-green-400' : 'text-text-muted'}`}>
                  <LandingIcon className="w-3 h-3" />
                  <span>Landings</span>
                  {cascadeStatus.drafts_generated?.landings && <CheckCircle2 className="w-3 h-3" />}
                </div>
                <div className={`flex items-center gap-1 ${cascadeStatus.drafts_generated?.emails ? 'text-green-400' : 'text-text-muted'}`}>
                  <Mail className="w-3 h-3" />
                  <span>Emails</span>
                  {cascadeStatus.drafts_generated?.emails && <CheckCircle2 className="w-3 h-3" />}
                </div>
                <div className={`flex items-center gap-1 ${cascadeStatus.drafts_generated?.ads ? 'text-green-400' : 'text-text-muted'}`}>
                  <Megaphone className="w-3 h-3" />
                  <span>Ads</span>
                  {cascadeStatus.drafts_generated?.ads && <CheckCircle2 className="w-3 h-3" />}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-display text-2xl font-bold text-text-main mb-2 flex items-center gap-3">
              <CheckCircle2 className="w-7 h-7 text-[#00D68F]" />
              Brief de Marketing Generado
            </h2>
            <p className="text-text-secondary">
              Tu estrategia de copywriting está lista. Haz clic en ✏️ para editar o 🔄 para regenerar.
            </p>
          </div>
          
          <Button
            variant="outline"
            onClick={resetToEdit}
            className="border-border-subtle text-text-secondary hover:text-text-main"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Editar Formulario
          </Button>
        </div>

        {/* Strategy Badge */}
        {strategyData && variantData && (
          <div className="flex items-center gap-3 bg-surface border border-border-subtle rounded-xl px-4 py-3 w-fit">
            <span className="text-2xl">{strategyData.icon}</span>
            <div>
              <p className="text-sm font-medium text-text-main">{strategyData.label} - {variantData.label}</p>
              <p className="text-xs text-text-muted">Producto: {productName} | Precio: {price}</p>
            </div>
          </div>
        )}

        {/* Results Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          {/* ==================== CARD 1: AVATAR ==================== */}
          <div className="bg-surface border border-border-subtle rounded-xl overflow-hidden group hover:border-brand/30 transition-all">
            <div className="bg-gradient-to-r from-brand/10 to-purple-600/10 px-5 py-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-brand/20 flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-brand-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-text-main">👤 Tu Avatar Ideal</h3>
                    <p className="text-xs text-text-muted">Cliente objetivo definido</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button 
                    onClick={() => regenerateCard('avatar')}
                    disabled={regeneratingCard === 'avatar' || editingCard === 'avatar'}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-2 hover:bg-brand/10 rounded-lg disabled:opacity-50"
                    title="Regenerar con IA"
                  >
                    <RefreshCw className={`w-4 h-4 text-brand-500 ${regeneratingCard === 'avatar' ? 'animate-spin' : ''}`} />
                  </button>
                  <button 
                    onClick={() => editingCard === 'avatar' ? cancelEditing() : startEditing('avatar')}
                    disabled={regeneratingCard === 'avatar'}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-2 hover:bg-brand/10 rounded-lg"
                    title={editingCard === 'avatar' ? 'Cancelar' : 'Editar'}
                  >
                    {editingCard === 'avatar' ? (
                      <XCircle className="w-4 h-4 text-red-400" />
                    ) : (
                      <Pencil className="w-4 h-4 text-brand-500" />
                    )}
                  </button>
                </div>
              </div>
            </div>
            
            {regeneratingCard === 'avatar' ? (
              <CardSkeleton colorClass="bg-brand-500" />
            ) : editingCard === 'avatar' ? (
              /* AVATAR EDIT MODE */
              <div className="p-5 space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-brand to-purple-600 flex items-center justify-center text-white font-bold text-lg">
                    {avatarEdit?.name?.charAt(0) || 'M'}
                  </div>
                  <div className="flex-1 space-y-2">
                    <Input
                      value={avatarEdit?.name || ''}
                      onChange={(e) => setAvatarEdit({ ...avatarEdit, name: e.target.value })}
                      placeholder="Nombre"
                      className="bg-app-bg border-border-subtle text-text-main h-8 text-sm"
                    />
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        value={avatarEdit?.age || ''}
                        onChange={(e) => setAvatarEdit({ ...avatarEdit, age: parseInt(e.target.value) || 0 })}
                        placeholder="Edad"
                        className="bg-app-bg border-border-subtle text-text-main h-8 text-sm w-20"
                      />
                      <Input
                        value={avatarEdit?.occupation || ''}
                        onChange={(e) => setAvatarEdit({ ...avatarEdit, occupation: e.target.value })}
                        placeholder="Ocupación"
                        className="bg-app-bg border-border-subtle text-text-main h-8 text-sm flex-1"
                      />
                    </div>
                  </div>
                </div>
                
                <div>
                  <p className="text-xs font-medium text-red-400 mb-2 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> MIEDOS
                  </p>
                  <EditableTags
                    tags={avatarEdit?.fears || []}
                    onChange={(newTags) => setAvatarEdit({ ...avatarEdit, fears: newTags })}
                    colorClass="text-red-400"
                    bgClass="bg-red-500/10"
                  />
                </div>
                
                <div>
                  <p className="text-xs font-medium text-[#00D68F] mb-2 flex items-center gap-1">
                    <Heart className="w-3 h-3" /> DESEOS
                  </p>
                  <EditableTags
                    tags={avatarEdit?.desires || []}
                    onChange={(newTags) => setAvatarEdit({ ...avatarEdit, desires: newTags })}
                    colorClass="text-[#00D68F]"
                    bgClass="bg-[#00D68F]/10"
                  />
                </div>
                
                {/* Edit Actions */}
                <div className="flex justify-end gap-2 pt-2 border-t border-border-subtle">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={cancelEditing}
                    className="text-text-muted hover:text-text-main"
                  >
                    Cancelar
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => saveEditing('avatar')}
                    className="bg-brand-500 hover:bg-brand-hover text-white"
                  >
                    <Save className="w-3 h-3 mr-1" />
                    Guardar
                  </Button>
                </div>
              </div>
            ) : (
              /* AVATAR VIEW MODE */
              <div className="p-5 space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-brand to-purple-600 flex items-center justify-center text-white font-bold text-lg">
                    {generatedContent.avatar?.name?.charAt(0) || 'M'}
                  </div>
                  <div>
                    <p className="font-medium text-text-main">{generatedContent.avatar?.name}</p>
                    <p className="text-sm text-text-muted">{generatedContent.avatar?.age} años • {generatedContent.avatar?.occupation}</p>
                  </div>
                </div>
                
                <div>
                  <p className="text-xs font-medium text-red-400 mb-2 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> MIEDOS
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {generatedContent.avatar?.fears?.map((fear) => (
                      <span key={fear} className="text-xs bg-red-500/10 text-red-400 px-3 py-1.5 rounded-full">
                        {fear}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div>
                  <p className="text-xs font-medium text-[#00D68F] mb-2 flex items-center gap-1">
                    <Heart className="w-3 h-3" /> DESEOS
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {generatedContent.avatar?.desires?.map((desire) => (
                      <span key={desire} className="text-xs bg-[#00D68F]/10 text-[#00D68F] px-3 py-1.5 rounded-full">
                        {desire}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ==================== CARD 2: VALUE PROPOSITION ==================== */}
          <div className="bg-surface border border-border-subtle rounded-xl overflow-hidden group hover:border-brand/30 transition-all">
            <div className="bg-gradient-to-r from-purple-600/10 to-pink-600/10 px-5 py-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                    <Gem className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-text-main">💎 Propuesta de Valor</h3>
                    <p className="text-xs text-text-muted">Tu diferencial único</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button 
                    onClick={() => regenerateCard('value')}
                    disabled={regeneratingCard === 'value' || editingCard === 'value'}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-2 hover:bg-purple-500/10 rounded-lg disabled:opacity-50"
                    title="Regenerar con IA"
                  >
                    <RefreshCw className={`w-4 h-4 text-purple-400 ${regeneratingCard === 'value' ? 'animate-spin' : ''}`} />
                  </button>
                  <button 
                    onClick={() => editingCard === 'value' ? cancelEditing() : startEditing('value')}
                    disabled={regeneratingCard === 'value'}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-2 hover:bg-purple-500/10 rounded-lg"
                    title={editingCard === 'value' ? 'Cancelar' : 'Editar'}
                  >
                    {editingCard === 'value' ? (
                      <XCircle className="w-4 h-4 text-red-400" />
                    ) : (
                      <Pencil className="w-4 h-4 text-purple-400" />
                    )}
                  </button>
                </div>
              </div>
            </div>
            
            {regeneratingCard === 'value' ? (
              <CardSkeleton colorClass="bg-purple-500" />
            ) : editingCard === 'value' ? (
              /* VALUE EDIT MODE */
              <div className="p-5 space-y-4">
                <div>
                  <p className="text-xs font-medium text-purple-400 mb-2">DIFERENCIAL VS COMPETENCIA</p>
                  <AutoExpandTextarea
                    value={valueEdit?.differential || ''}
                    onChange={(e) => setValueEdit({ ...valueEdit, differential: e.target.value })}
                    placeholder="¿Qué te hace diferente?"
                    className="w-full bg-app-bg border border-border-subtle rounded-lg p-3 text-sm text-text-main placeholder:text-text-muted focus:outline-none focus:border-purple-400"
                  />
                </div>
                <div>
                  <p className="text-xs font-medium text-pink-400 mb-2">ÁNGULO DE VENTAS</p>
                  <AutoExpandTextarea
                    value={valueEdit?.salesAngle || ''}
                    onChange={(e) => setValueEdit({ ...valueEdit, salesAngle: e.target.value })}
                    placeholder="Tu ángulo persuasivo..."
                    className="w-full bg-app-bg border border-border-subtle rounded-lg p-3 text-sm text-text-main placeholder:text-text-muted focus:outline-none focus:border-pink-400"
                  />
                </div>
                
                {/* Edit Actions */}
                <div className="flex justify-end gap-2 pt-2 border-t border-border-subtle">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={cancelEditing}
                    className="text-text-muted hover:text-text-main"
                  >
                    Cancelar
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => saveEditing('value')}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    <Save className="w-3 h-3 mr-1" />
                    Guardar
                  </Button>
                </div>
              </div>
            ) : (
              /* VALUE VIEW MODE */
              <div className="p-5 space-y-4">
                <div>
                  <p className="text-xs font-medium text-purple-400 mb-2">DIFERENCIAL VS COMPETENCIA</p>
                  <p className="text-sm text-text-secondary leading-relaxed">
                    {generatedContent.valueProposition?.differential}
                  </p>
                </div>
                <div className="border-t border-border-subtle pt-4">
                  <p className="text-xs font-medium text-pink-400 mb-2">ÁNGULO DE VENTAS</p>
                  <p className="text-sm text-text-secondary leading-relaxed">
                    {generatedContent.valueProposition?.salesAngle}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* ==================== CARD 3: OFFER ==================== */}
          <div className="bg-surface border border-border-subtle rounded-xl overflow-hidden group hover:border-brand/30 transition-all">
            <div className="bg-gradient-to-r from-[#00D68F]/10 to-teal-600/10 px-5 py-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#00D68F]/20 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-[#00D68F]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-text-main">🛡️ Tu Oferta Irresistible</h3>
                    <p className="text-xs text-text-muted">Garantía y bonos</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button 
                    onClick={() => regenerateCard('offer')}
                    disabled={regeneratingCard === 'offer' || editingCard === 'offer'}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-2 hover:bg-[#00D68F]/10 rounded-lg disabled:opacity-50"
                    title="Regenerar con IA"
                  >
                    <RefreshCw className={`w-4 h-4 text-[#00D68F] ${regeneratingCard === 'offer' ? 'animate-spin' : ''}`} />
                  </button>
                  <button 
                    onClick={() => editingCard === 'offer' ? cancelEditing() : startEditing('offer')}
                    disabled={regeneratingCard === 'offer'}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-2 hover:bg-[#00D68F]/10 rounded-lg"
                    title={editingCard === 'offer' ? 'Cancelar' : 'Editar'}
                  >
                    {editingCard === 'offer' ? (
                      <XCircle className="w-4 h-4 text-red-400" />
                    ) : (
                      <Pencil className="w-4 h-4 text-[#00D68F]" />
                    )}
                  </button>
                </div>
              </div>
            </div>
            
            {regeneratingCard === 'offer' ? (
              <CardSkeleton colorClass="bg-[#00D68F]" />
            ) : editingCard === 'offer' ? (
              /* OFFER EDIT MODE */
              <div className="p-5 space-y-4">
                <div>
                  <p className="text-xs font-medium text-[#00D68F] mb-2 flex items-center gap-1">
                    <Shield className="w-3 h-3" /> GARANTÍA
                  </p>
                  <AutoExpandTextarea
                    value={offerEdit?.guarantee || ''}
                    onChange={(e) => setOfferEdit({ ...offerEdit, guarantee: e.target.value })}
                    placeholder="Tu garantía..."
                    className="w-full bg-[#00D68F]/5 border border-[#00D68F]/20 rounded-lg p-3 text-sm text-text-main placeholder:text-text-muted focus:outline-none focus:border-[#00D68F]"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-medium text-amber-400 flex items-center gap-1">
                      <Gift className="w-3 h-3" /> BONOS
                    </p>
                    <button
                      onClick={addBonus}
                      className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1"
                    >
                      <Plus className="w-3 h-3" /> Añadir bono
                    </button>
                  </div>
                  <div className="space-y-3">
                    {offerEdit?.bonuses?.map((bonus, i) => (
                      <div key={`bonus-edit-${bonus.name || i}`} className="bg-app-bg/50 p-3 rounded-lg border border-border-subtle relative group/bonus">
                        <button
                          onClick={() => removeBonus(i)}
                          className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-500/80 text-white flex items-center justify-center opacity-0 group-hover/bonus:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                        <div className="flex gap-2 mb-2">
                          <Input
                            value={bonus.name}
                            onChange={(e) => updateBonus(i, 'name', e.target.value)}
                            placeholder="Nombre del bono"
                            className="bg-transparent border-border-subtle text-text-main h-8 text-sm flex-1"
                          />
                          <Input
                            value={bonus.value}
                            onChange={(e) => updateBonus(i, 'value', e.target.value)}
                            placeholder="Valor"
                            className="bg-transparent border-border-subtle text-amber-400 h-8 text-sm w-24 font-bold"
                          />
                        </div>
                        <Input
                          value={bonus.description}
                          onChange={(e) => updateBonus(i, 'description', e.target.value)}
                          placeholder="Descripción breve..."
                          className="bg-transparent border-border-subtle text-text-muted h-8 text-xs"
                        />
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Edit Actions */}
                <div className="flex justify-end gap-2 pt-2 border-t border-border-subtle">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={cancelEditing}
                    className="text-text-muted hover:text-text-main"
                  >
                    Cancelar
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => saveEditing('offer')}
                    className="bg-[#00D68F] hover:bg-[#00C080] text-white"
                  >
                    <Save className="w-3 h-3 mr-1" />
                    Guardar
                  </Button>
                </div>
              </div>
            ) : (
              /* OFFER VIEW MODE */
              <div className="p-5 space-y-4">
                <div>
                  <p className="text-xs font-medium text-[#00D68F] mb-2 flex items-center gap-1">
                    <Shield className="w-3 h-3" /> GARANTÍA
                  </p>
                  <p className="text-sm text-text-secondary bg-[#00D68F]/5 p-3 rounded-lg border border-[#00D68F]/20">
                    {generatedContent.offer?.guarantee}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-amber-400 mb-2 flex items-center gap-1">
                    <Gift className="w-3 h-3" /> BONOS SUGERIDOS
                  </p>
                  <div className="space-y-2">
                    {generatedContent.offer?.bonuses?.map((bonus) => (
                      <div key={`bonus-${bonus.name}`} className="bg-app-bg/50 p-3 rounded-lg border border-border-subtle">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-sm font-medium text-text-main">{bonus.name}</p>
                          <span className="text-xs text-amber-400 font-bold">Valor: {bonus.value}</span>
                        </div>
                        <p className="text-xs text-text-muted">{bonus.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ==================== CARD 4: TITLES (SUPER-TARJETA) ==================== */}
          <div className="bg-surface border border-border-subtle rounded-xl overflow-hidden group hover:border-brand/30 transition-all">
            <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 px-5 py-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                    <Trophy className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-text-main">🏆 Títulos Ganadores</h3>
                    <p className="text-xs text-text-muted">Selecciona y personaliza tu favorito</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {editingCard !== 'titles' && (
                    <button 
                      onClick={() => startEditing('titles')}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-brand-500 bg-brand/10 hover:bg-brand/20 rounded-lg transition-all whitespace-nowrap"
                      title="Editar todos los títulos"
                      data-testid="edit-titles-btn"
                    >
                      <Pencil className="w-3 h-3" />
                      Editar
                    </button>
                  )}
                  <button 
                    onClick={generateNewTitles}
                    disabled={regeneratingCard === 'titles' || regeneratingCard === 'titles-variation'}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-400 bg-amber-500/10 hover:bg-amber-500/20 rounded-lg transition-all disabled:opacity-50 whitespace-nowrap"
                  >
                    <RefreshCw className={`w-3 h-3 ${regeneratingCard === 'titles' ? 'animate-spin' : ''}`} />
                    Generar 3 nuevos
                  </button>
                </div>
              </div>
            </div>
            
            {(regeneratingCard === 'titles' || regeneratingCard === 'titles-variation') ? (
              <CardSkeleton colorClass="bg-amber-500" />
            ) : (
              <div className="p-5 space-y-3">
                {generatedContent.titles?.map((title, i) => (
                  <div
                    key={`title-${title.slice(0, 20)}-${i}`}
                    className={`relative p-4 rounded-lg border-2 transition-all group/title ${
                      selectedTitle === String(i)
                        ? 'border-brand-500 bg-brand/5'
                        : 'border-border-subtle hover:border-brand/30'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <button
                        onClick={() => setSelectedTitle(String(i))}
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5 transition-all ${
                          selectedTitle === String(i)
                            ? 'border-brand-500 bg-brand'
                            : 'border-border-subtle'
                        }`}
                      >
                        {selectedTitle === String(i) && (
                          <div className="w-2 h-2 rounded-full bg-white" />
                        )}
                      </button>
                      
                      {editingCard === 'titles' ? (
                        <AutoExpandTextarea
                          value={titlesEdit?.[i] || ''}
                          onChange={(e) => {
                            const newTitles = [...titlesEdit];
                            newTitles[i] = e.target.value;
                            setTitlesEdit(newTitles);
                          }}
                          className="flex-1 bg-transparent border-none text-sm text-text-main focus:outline-none resize-none"
                        />
                      ) : (
                        <p 
                          className="text-sm text-text-main flex-1 cursor-pointer"
                          onClick={() => setSelectedTitle(String(i))}
                        >
                          {title}
                        </p>
                      )}
                      
                      {/* Action buttons - always visible on hover, prominent when selected */}
                      {!editingCard && (
                        <div className={`flex items-center gap-1 shrink-0 transition-opacity ${
                          selectedTitle === String(i) ? 'opacity-100' : 'opacity-0 group-hover/title:opacity-100'
                        }`}>
                          <button
                            onClick={() => {
                              setSelectedTitle(String(i));
                              startEditing('titles');
                            }}
                            className="p-1.5 hover:bg-brand/10 rounded-lg transition-colors"
                            title="Editar título"
                          >
                            <Pencil className="w-3.5 h-3.5 text-brand-500" />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedTitle(String(i));
                              generateVariations(i);
                            }}
                            className="p-1.5 hover:bg-amber-500/10 rounded-lg transition-colors group/sparkle"
                            title="Crear variaciones de este título"
                          >
                            <Sparkles className="w-3.5 h-3.5 text-amber-400 group-hover/sparkle:animate-pulse" />
                          </button>
                          {selectedTitle === String(i) && (
                            <CheckCircle2 className="w-5 h-5 text-brand-500 ml-1" />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                
                {/* Edit Mode Actions for Titles */}
                {editingCard === 'titles' && (
                  <div className="flex justify-end gap-2 pt-3 border-t border-border-subtle">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={cancelEditing}
                      className="text-text-muted hover:text-text-main"
                    >
                      Cancelar
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => saveEditing('titles')}
                      className="bg-amber-500 hover:bg-amber-600 text-white"
                    >
                      <Save className="w-3 h-3 mr-1" />
                      Guardar títulos
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Continue Button */}
        <div className="bg-gradient-to-r from-brand/10 to-[#00D68F]/10 border border-brand/30 rounded-xl p-6">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#00D68F]/20 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-6 h-6 text-[#00D68F]" />
              </div>
              <div>
                <h3 className="font-semibold text-text-main">Brief Completo</h3>
                <p className="text-sm text-text-muted">
                  Todo listo para generar el copy de tus landings
                </p>
              </div>
            </div>
            
            <Button
              onClick={onContinueToLandings}
              disabled={!selectedClient || !funnelName}
              className="bg-gradient-to-r from-brand to-[#00D68F] hover:from-brand-hover hover:to-[#00C080] text-white px-6 py-3 h-auto text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex-shrink-0"
              data-testid="continue-to-landings-btn"
            >
              Continuar a Landings
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
          {(!selectedClient || !funnelName) && (
            <p className="text-amber-400 text-sm mt-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Selecciona un cliente y nombre del funnel antes de continuar
            </p>
          )}
        </div>
      </div>
    );
  }

  // ==================== TEMPLATE GALLERY HANDLERS ====================
  const handleSelectTemplate = (templateBrief) => {
    // Apply template data to form fields - use existing state setters
    setProductName(templateBrief.product_name || '');
    setWhatYouSell(templateBrief.product_description || '');
    setTargetAudience(templateBrief.target_audience || '');
    setMainPromise(templateBrief.main_promise || '');
    setPainPoints(templateBrief.pain_points || '');
    setMainObjections(templateBrief.main_objections || '');
    setPrice(templateBrief.price || '');
    setToneOfVoice(templateBrief.tone_of_voice || 'cercano');
    setExpertName(templateBrief.expert_name || '');
    setOfferDetails(templateBrief.bonuses || '');
    setDiasGarantia(templateBrief.dias_garantia || '14');
    setCuentaAtrasOferta(templateBrief.cuenta_atras_oferta || '48');
    setDiscountPercent(templateBrief.discount_percent || '');
    setShippingConditions(templateBrief.shipping_conditions || '');
    setCoreFeatures(templateBrief.core_features || '');
    
    // Mark gallery as seen
    localStorage.setItem('brief_template_gallery_seen', 'true');
    
    toast.success('Plantilla aplicada', {
      description: 'Ahora puedes editar los datos para adaptarlos a tu caso'
    });
  };

  const handleStartFromScratch = () => {
    localStorage.setItem('brief_template_gallery_seen', 'true');
  };

  const handleCloseTemplateGallery = () => {
    setShowTemplateGallery(false);
    localStorage.setItem('brief_template_gallery_seen', 'true');
  };

  // ==================== FORM VIEW ====================
  return (
    <div data-testid="brief-step" className="space-y-6">
      {/* Template Gallery Modal */}
      <BriefTemplateGallery
        isVisible={showTemplateGallery}
        onSelectTemplate={handleSelectTemplate}
        onStartFromScratch={handleStartFromScratch}
        onClose={handleCloseTemplateGallery}
      />

      {/* First-time user hint */}
      <FirstTimeHint
        id="brief_step_intro"
        message="¿No sabes qué poner? Empieza por lo básico: el nombre de tu producto y a quién va dirigido. La IA rellenará el resto con sugerencias que puedes editar."
      />

      {/* Header with Strategy Info */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-bold text-text-main mb-2">
            Brief del Proyecto
          </h2>
          <p className="text-text-secondary">
            Completa la información para generar copy personalizado con IA
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Template Gallery Button */}
          <Button
            variant="outline"
            onClick={() => setShowTemplateGallery(true)}
            className="border-brand-200 dark:border-brand-800 hover:bg-brand-50 dark:hover:bg-brand-900/20"
          >
            <LayoutTemplate className="w-4 h-4 mr-2 text-brand-500" />
            Ver plantillas
          </Button>
          
          {strategyData && variantData && (
            <div className="hidden sm:flex items-center gap-3 bg-surface border border-border-subtle rounded-xl px-4 py-3">
              <span className="text-2xl">{strategyData.icon}</span>
              <div>
                <p className="text-sm font-medium text-text-main">{strategyData.label}</p>
                <p className="text-xs text-text-muted">{variantData.label}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ==================== SECTION A: BASE FIELDS ==================== */}
      <div className="bg-surface border border-border-subtle rounded-xl p-6 space-y-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-brand/10 flex items-center justify-center">
            <FileText className="w-5 h-5 text-brand-500" />
          </div>
          <div>
            <h3 className="font-semibold text-text-main">Información Base</h3>
            <p className="text-sm text-text-muted">Datos principales de tu producto o servicio</p>
          </div>
        </div>

        {/* Client & Funnel Name */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-text-secondary">Cliente</Label>
            <Select value={selectedClient || ''} onValueChange={onSelectClient}>
              <SelectTrigger className="bg-app-bg border-border-subtle text-text-main">
                <SelectValue placeholder="Selecciona un cliente" />
              </SelectTrigger>
              <SelectContent className="bg-surface border-border-subtle">
                {clients?.map((client) => (
                  <SelectItem key={client.id} value={client.id}>
                    {client.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label className="text-text-secondary">Nombre del Funnel</Label>
            <Input
              value={funnelName}
              onChange={(e) => onSetFunnelName(e.target.value)}
              placeholder="Ej: Lanzamiento Curso Marketing 2025"
              className="bg-app-bg border-border-subtle text-text-main placeholder:text-text-muted focus:border-brand-500"
            />
          </div>
        </div>

        {/* Website URL */}
        <div className="space-y-2">
          <Label className="text-text-secondary flex items-center gap-2">
            <Globe className="w-4 h-4" />
            URL de la Web (opcional)
          </Label>
          <div className="flex gap-2">
            <Input
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              onBlur={handleWebsiteUrlBlur}
              placeholder="https://tuproducto.com"
              className="bg-app-bg border-border-subtle text-text-main placeholder:text-text-muted focus:border-brand-500 flex-1"
            />
            <Button
              onClick={analyzeWebsite}
              disabled={analyzingWeb || !websiteUrl}
              variant="outline"
              className="border-brand/50 text-brand-500 hover:bg-brand/10 hover:border-brand-500"
            >
              {analyzingWeb ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Search className="w-4 h-4 mr-2" />
                  Analizar web
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Niche & Product */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-text-secondary">Nicho / Sector</Label>
            <Select value={niche} onValueChange={setNiche}>
              <SelectTrigger className="bg-app-bg border-border-subtle text-text-main">
                <SelectValue placeholder="Selecciona un nicho" />
              </SelectTrigger>
              <SelectContent className="bg-surface border-border-subtle max-h-[300px]">
                {NICHES.map((n) => (
                  <SelectItem key={n.id} value={n.id}>
                    {n.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label className={`text-text-secondary ${showValidationErrors && isProductNameEmpty ? 'text-red-400' : ''}`}>
              Nombre del Producto/Servicio <span className="text-red-400">*</span>
            </Label>
            <Input
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              placeholder="Ej: Curso de Marketing Digital Pro"
              className={`bg-app-bg text-text-main placeholder:text-text-muted focus:border-brand-500 ${
                showValidationErrors && isProductNameEmpty 
                  ? 'border-red-500 ring-1 ring-red-500/50' 
                  : 'border-border-subtle'
              }`}
            />
            {showValidationErrors && isProductNameEmpty && (
              <p className="text-red-400 text-xs mt-1">Este campo es obligatorio</p>
            )}
          </div>
        </div>

        {/* Price & Upsell */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label className="text-text-secondary flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Precio Base *
            </Label>
            <Input
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="497€"
              className="bg-app-bg border-border-subtle text-text-main placeholder:text-text-muted focus:border-brand-500"
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-text-secondary">¿Tiene Upsell?</Label>
            <div className="flex items-center gap-3 h-10">
              <Switch
                checked={hasUpsell}
                onCheckedChange={setHasUpsell}
                className="data-[state=checked]:bg-brand-500"
              />
              <span className="text-text-muted text-sm">{hasUpsell ? 'Sí' : 'No'}</span>
            </div>
          </div>

          {hasUpsell && (
            <div className="space-y-2">
              <Label className="text-text-secondary">Upsell</Label>
              <div className="flex gap-2">
                <Input
                  value={upsellName}
                  onChange={(e) => setUpsellName(e.target.value)}
                  placeholder="VIP Pack"
                  className="bg-app-bg border-border-subtle text-text-main placeholder:text-text-muted focus:border-brand-500 flex-1"
                />
                <Input
                  value={upsellPrice}
                  onChange={(e) => setUpsellPrice(e.target.value)}
                  placeholder="997€"
                  className="bg-app-bg border-border-subtle text-text-main placeholder:text-text-muted focus:border-brand-500 w-24"
                />
              </div>
            </div>
          )}
        </div>

        {/* Core Questions */}
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-text-secondary flex items-center gap-2">
              <FileText className="w-4 h-4" />
              ¿Qué vendes exactamente? *
            </Label>
            <Textarea
              value={whatYouSell}
              onChange={(e) => setWhatYouSell(e.target.value)}
              placeholder="Un curso online de 8 módulos que enseña estrategias avanzadas de marketing digital..."
              rows={2}
              className="bg-app-bg border-border-subtle text-text-main placeholder:text-text-muted focus:border-brand-500 resize-none"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-text-secondary flex items-center gap-2">
              <Users className="w-4 h-4" />
              ¿A quién se lo vendes? *
            </Label>
            <Textarea
              value={targetAudience}
              onChange={(e) => setTargetAudience(e.target.value)}
              placeholder="Emprendedores y dueños de pequeños negocios que quieren aprender a captar clientes online..."
              rows={2}
              className="bg-app-bg border-border-subtle text-text-main placeholder:text-text-muted focus:border-brand-500 resize-none"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-text-secondary flex items-center gap-2">
              <Target className="w-4 h-4" />
              Promesa principal o resultado
            </Label>
            <Textarea
              value={mainPromise}
              onChange={(e) => setMainPromise(e.target.value)}
              placeholder="Conseguir sus primeros 100 clientes usando publicidad en redes sociales..."
              rows={2}
              className="bg-app-bg border-border-subtle text-text-main placeholder:text-text-muted focus:border-brand-500 resize-none"
            />
          </div>
          
          {/* NEW STRATEGIC FIELDS */}
          <div className="space-y-2">
            <Label className="text-text-secondary flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-400" />
              Dolores / Puntos de dolor
            </Label>
            <Textarea
              value={painPoints}
              onChange={(e) => setPainPoints(e.target.value)}
              placeholder="No consiguen clientes; Gastan dinero en ads sin resultados; No saben qué contenido crear..."
              rows={2}
              className="bg-app-bg border-border-subtle text-text-main placeholder:text-text-muted focus:border-brand-500 resize-none"
            />
            <p className="text-xs text-text-muted">Problemas que tu producto resuelve (separados por punto y coma)</p>
          </div>
          
          <div className="space-y-2">
            <Label className="text-text-secondary flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-green-400" />
              ¿Cómo resuelves el problema?
            </Label>
            <Textarea
              value={solutionOverview}
              onChange={(e) => setSolutionOverview(e.target.value)}
              placeholder="Con un método paso a paso probado que ha funcionado para más de 500 alumnos..."
              rows={2}
              className="bg-app-bg border-border-subtle text-text-main placeholder:text-text-muted focus:border-brand-500 resize-none"
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-text-secondary flex items-center gap-2">
              <Shield className="w-4 h-4 text-amber-400" />
              Objeciones principales
            </Label>
            <Textarea
              value={mainObjections}
              onChange={(e) => setMainObjections(e.target.value)}
              placeholder="No tengo tiempo; Es muy caro; Ya lo intenté y no funcionó; No sé si funcionará para mi caso..."
              rows={2}
              className="bg-app-bg border-border-subtle text-text-main placeholder:text-text-muted focus:border-brand-500 resize-none"
            />
            <p className="text-xs text-text-muted">Razones por las que NO comprarían</p>
          </div>
          
          <div className="space-y-2">
            <Label className="text-text-secondary flex items-center gap-2">
              <Gift className="w-4 h-4 text-purple-400" />
              Detalles de la oferta
            </Label>
            <Textarea
              value={offerDetails}
              onChange={(e) => setOfferDetails(e.target.value)}
              placeholder="8 módulos, 50+ lecciones, plantillas editables, comunidad privada, 3 sesiones en vivo, garantía 30 días..."
              rows={2}
              className="bg-app-bg border-border-subtle text-text-main placeholder:text-text-muted focus:border-brand-500 resize-none"
            />
            <p className="text-xs text-text-muted">Qué incluye la oferta, bonos, garantías</p>
          </div>
          
          <div className="space-y-2">
            <Label className="text-text-secondary flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-cyan-400" />
              Tono de voz
            </Label>
            <Select value={toneOfVoice} onValueChange={setToneOfVoice}>
              <SelectTrigger className="bg-app-bg border-border-subtle text-text-main">
                <SelectValue placeholder="Selecciona un tono" />
              </SelectTrigger>
              <SelectContent className="bg-surface border-border-subtle">
                <SelectItem value="cercano">Cercano y amigable</SelectItem>
                <SelectItem value="formal">Formal y profesional</SelectItem>
                <SelectItem value="directo">Directo y sin rodeos</SelectItem>
                <SelectItem value="urgente">Urgente y escasez</SelectItem>
                <SelectItem value="inspiracional">Inspiracional y motivador</SelectItem>
                <SelectItem value="irreverente">Irreverente y provocador</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-text-muted">Cómo quieres que suene tu marca</p>
          </div>
        </div>
      </div>

      {/* ==================== SECTION: BRANDING GLOBAL ==================== */}
      <div className="bg-surface border border-border-subtle rounded-xl p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500/20 to-purple-500/20 flex items-center justify-center">
              <Palette className="w-5 h-5 text-pink-400" />
            </div>
            <div>
              <h3 className="font-semibold text-text-main">Branding del Proyecto</h3>
              <p className="text-sm text-text-muted">Colores y logo para emails, landings y creativos</p>
            </div>
          </div>
          {isSaving && (
            <span className="text-xs text-text-muted flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" />
              Guardando...
            </span>
          )}
          {lastSaved && !isSaving && (
            <span className="text-xs text-green-400 flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" />
              Guardado
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Primary Color */}
          <div className="space-y-2">
            <Label className="text-text-secondary flex items-center gap-2">
              <div className="w-3 h-3 rounded-full border border-border-subtle" style={{ backgroundColor: primaryColor || '#ccc' }} />
              Color Primario
            </Label>
            <div className="flex gap-2">
              <Input
                type="color"
                value={primaryColor || '#6366F1'}
                onChange={(e) => setPrimaryColor(e.target.value)}
                className="w-12 h-10 p-1 bg-app-bg border-border-subtle cursor-pointer"
              />
              <Input
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                placeholder="Sin color definido"
                className="bg-app-bg border-border-subtle text-text-main font-mono text-sm flex-1"
              />
            </div>
            <p className="text-xs text-text-muted">Botones y titulares</p>
          </div>

          {/* Secondary Color */}
          <div className="space-y-2">
            <Label className="text-text-secondary flex items-center gap-2">
              <div className="w-3 h-3 rounded-full border border-border-subtle" style={{ backgroundColor: secondaryColor || '#ccc' }} />
              Color Secundario
            </Label>
            <div className="flex gap-2">
              <Input
                type="color"
                value={secondaryColor || '#22C55E'}
                onChange={(e) => setSecondaryColor(e.target.value)}
                className="w-12 h-10 p-1 bg-app-bg border-border-subtle cursor-pointer"
              />
              <Input
                value={secondaryColor}
                onChange={(e) => setSecondaryColor(e.target.value)}
                placeholder="Sin color definido"
                className="bg-app-bg border-border-subtle text-text-main font-mono text-sm flex-1"
              />
            </div>
            <p className="text-xs text-text-muted">Acentos y fondos</p>
          </div>
          
          {/* Accent Color (NUEVO) */}
          <div className="space-y-2">
            <Label className="text-text-secondary flex items-center gap-2">
              <div className="w-3 h-3 rounded-full border border-border-subtle" style={{ backgroundColor: accentColor || '#ccc' }} />
              Color Acento
            </Label>
            <div className="flex gap-2">
              <Input
                type="color"
                value={accentColor || '#F59E0B'}
                onChange={(e) => setAccentColor(e.target.value)}
                className="w-12 h-10 p-1 bg-app-bg border-border-subtle cursor-pointer"
              />
              <Input
                value={accentColor}
                onChange={(e) => setAccentColor(e.target.value)}
                placeholder="Sin color definido"
                className="bg-app-bg border-border-subtle text-text-main font-mono text-sm flex-1"
              />
            </div>
            <p className="text-xs text-text-muted">Destacados y CTAs</p>
          </div>

          {/* Auto-detect button */}
          <div className="space-y-2">
            <Label className="text-text-secondary">Autodetección</Label>
            <Button
              variant="outline"
              size="sm"
              onClick={extractBrandColors}
              disabled={!websiteUrl || extractingBrandColors}
              className="w-full h-10 border-border-subtle text-text-muted hover:text-brand-500 hover:border-brand-500"
            >
              {extractingBrandColors ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Detectando...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Detectar de la web
                </>
              )}
            </Button>
            <p className="text-xs text-text-muted">Extrae colores de tu URL</p>
          </div>
        </div>
        
        {/* Logo del Proyecto */}
        <div className="mt-6 space-y-2">
          <Label className="text-text-secondary flex items-center gap-2">
            <ImageIcon className="w-4 h-4" />
            Logo del Proyecto
          </Label>
          <div className="flex gap-2">
              <Input
                value={projectLogo}
                onChange={(e) => setProjectLogo(e.target.value)}
                placeholder="URL del logo o sube uno"
                className="bg-app-bg border-border-subtle text-text-main text-sm flex-1"
              />
              <input
                type="file"
                id="logo-upload"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    if (file.size > 2 * 1024 * 1024) {
                      toast.error('El logo debe ser menor a 2MB');
                      return;
                    }
                    setUploadingLogo(true);
                    const reader = new FileReader();
                    reader.onloadend = () => {
                      setProjectLogo(reader.result);
                      setUploadingLogo(false);
                      toast.success('Logo subido correctamente');
                    };
                    reader.onerror = () => {
                      toast.error('Error al subir el logo');
                      setUploadingLogo(false);
                    };
                    reader.readAsDataURL(file);
                  }
                }}
              />
              <Button
                variant="outline"
                size="sm"
                className="border-border-subtle text-text-muted hover:text-brand-500 shrink-0"
                disabled={uploadingLogo}
                onClick={() => document.getElementById('logo-upload').click()}
              >
                {uploadingLogo ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
              </Button>
            </div>
          {projectLogo && (
            <div className="mt-2 p-2 bg-app-bg rounded-lg flex items-center gap-2">
              <img 
                src={projectLogo} 
                alt="Logo preview" 
                className="w-8 h-8 object-contain rounded"
                onError={(e) => e.target.style.display = 'none'}
              />
              <span className="text-xs text-text-muted truncate flex-1">
                {projectLogo.startsWith('data:') ? 'Logo subido' : projectLogo}
              </span>
              <button 
                onClick={() => setProjectLogo('')}
                className="text-text-muted hover:text-red-400"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          <p className="text-xs text-text-muted">Sube una imagen o pega una URL. Max 2MB.</p>
        </div>
        
        {/* Estilo Visual de Marca */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          <div className="space-y-2">
            <Label className="text-text-secondary flex items-center gap-2">
              <Palette className="w-4 h-4" />
              Estilo Visual de Marca
            </Label>
            <textarea
              value={marcaEstiloVisual}
              onChange={(e) => setMarcaEstiloVisual(e.target.value)}
              placeholder="minimalista, moderno, mucho aire, degradados suaves, tech cercano"
              className="w-full h-20 px-3 py-2 bg-app-bg border border-border-subtle rounded-lg text-text-main text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-500/50"
            />
            <p className="text-xs text-text-muted">Describe el estilo visual deseado para los creativos</p>
          </div>
          
          <div className="space-y-2">
            <Label className="text-text-secondary flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Elementos a Evitar
            </Label>
            <textarea
              value={marcaEvitar}
              onChange={(e) => setMarcaEvitar(e.target.value)}
              placeholder="stock corporativo, personas falsas sonriendo, iconos cliché, saturación excesiva, texto en la imagen"
              className="w-full h-20 px-3 py-2 bg-app-bg border border-border-subtle rounded-lg text-text-main text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-500/50"
            />
            <p className="text-xs text-text-muted">Elementos que NO deben aparecer en tus creativos</p>
          </div>
        </div>

        {/* Preview */}
        <div className="p-4 rounded-xl border border-border-subtle mt-6" style={{ 
          background: `linear-gradient(135deg, ${primaryColor || '#6366F1'}10, ${secondaryColor || '#22C55E'}10)` 
        }}>
          <p className="text-xs text-text-muted mb-2">Vista previa</p>
          <div className="flex items-center gap-4 flex-wrap">
            <button 
              className="px-4 py-2 rounded-lg text-white text-sm font-medium transition-all hover:opacity-90"
              style={{ backgroundColor: primaryColor || '#6366F1' }}
            >
              Botón Principal
            </button>
            <button 
              className="px-4 py-2 rounded-lg text-white text-sm font-medium transition-all hover:opacity-90"
              style={{ backgroundColor: secondaryColor || '#22C55E' }}
            >
              Botón Secundario
            </button>
            <button 
              className="px-4 py-2 rounded-lg text-white text-sm font-medium transition-all hover:opacity-90"
              style={{ backgroundColor: accentColor || '#F59E0B' }}
            >
              Acento
            </button>
            <span className="text-sm font-semibold" style={{ color: primaryColor || '#6366F1' }}>
              Titular de ejemplo
            </span>
          </div>
        </div>
      </div>

      {/* ==================== SECTION: META PIXEL TRACKING ==================== */}
      <div className="bg-surface border border-border-subtle rounded-xl p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500/20 to-indigo-500/20 flex items-center justify-center">
            <Target className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h3 className="font-semibold text-text-main">Meta Pixel (Facebook/Instagram)</h3>
            <p className="text-sm text-text-muted">Tracking de conversiones para tus campañas de Meta Ads</p>
          </div>
        </div>
        
        <div className="space-y-2">
          <Label className="text-text-secondary flex items-center gap-2">
            <Megaphone className="w-4 h-4" />
            Pixel ID
          </Label>
          <Input
            value={metaPixelId}
            onChange={(e) => {
              // Solo permitir números
              const value = e.target.value.replace(/\D/g, '');
              setMetaPixelId(value);
            }}
            placeholder="Ej: 123456789012345"
            className="bg-app-bg border-border-subtle text-text-main font-mono placeholder:text-text-muted focus:border-brand-500"
            data-testid="meta-pixel-id-input"
          />
          <p className="text-xs text-text-muted">
            Encuéntralo en <a 
              href="https://business.facebook.com/events_manager" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-400 hover:underline"
            >
              Meta Events Manager
            </a> → Conectar datos → Pixel Web
          </p>
        </div>
        
        {metaPixelId && (
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
            <p className="text-sm text-blue-300 font-medium mb-2">✓ Pixel configurado</p>
            <p className="text-xs text-text-muted">
              Se inyectará automáticamente en todas las landings generadas con los siguientes eventos:
            </p>
            <ul className="text-xs text-text-muted mt-2 space-y-1 ml-4 list-disc">
              <li><span className="text-white">ViewContent</span> - Páginas de VSL/Ventas</li>
              <li><span className="text-white">Lead</span> - Al enviar formulario de opt-in</li>
              <li><span className="text-white">InitiateCheckout</span> - Página de checkout (con valor)</li>
              <li><span className="text-white">Purchase</span> - Página de gracias (con valor)</li>
            </ul>
          </div>
        )}
      </div>

      {/* ==================== SECTION B: DYNAMIC FIELDS ==================== */}
      {(needsEventFields || needsEcommerceFields || needsSaaSFields || needsLocalFields || needsHighTicketFields || needsIAFields) && (
        <div className="bg-surface border border-brand/20 rounded-xl p-6 space-y-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-brand/10 flex items-center justify-center">
              <Settings className="w-5 h-5 text-brand-500" />
            </div>
            <div>
              <h3 className="font-semibold text-text-main">Datos Específicos</h3>
              <p className="text-sm text-text-muted">
                Campos adicionales para estrategia &ldquo;{strategyData?.label || strategy}&rdquo;
              </p>
            </div>
          </div>

          {needsEventFields && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label className="text-text-secondary flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Fecha del Evento
                </Label>
                <Input
                  type="date"
                  value={eventDate}
                  onChange={(e) => setEventDate(e.target.value)}
                  className="bg-app-bg border-border-subtle text-text-main focus:border-brand-500"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-text-secondary flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Hora del Evento
                </Label>
                <Input
                  type="time"
                  value={eventTime}
                  onChange={(e) => setEventTime(e.target.value)}
                  className="bg-app-bg border-border-subtle text-text-main focus:border-brand-500"
                />
              </div>
              <div className="space-y-2">
                <Label className={`text-text-secondary flex items-center gap-2 ${showValidationErrors && isExpertNameEmpty ? 'text-red-400' : ''}`}>
                  <User className="w-4 h-4" />
                  Nombre del Experto/Host <span className="text-red-400">*</span>
                </Label>
                <Input
                  value={expertName}
                  onChange={(e) => setExpertName(e.target.value)}
                  placeholder="Ana García"
                  className={`bg-app-bg text-text-main placeholder:text-text-muted focus:border-brand-500 ${
                    showValidationErrors && isExpertNameEmpty 
                      ? 'border-red-500 ring-1 ring-red-500/50' 
                      : 'border-border-subtle'
                  }`}
                />
                {showValidationErrors && isExpertNameEmpty && (
                  <p className="text-red-400 text-xs mt-1">Este campo es obligatorio</p>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-text-secondary flex items-center gap-2">
                  <Video className="w-4 h-4" />
                  Plataforma
                </Label>
                <Select value={platform} onValueChange={setPlatform}>
                  <SelectTrigger className="bg-app-bg border-border-subtle text-text-main">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-surface border-border-subtle">
                    <SelectItem value="zoom">Zoom</SelectItem>
                    <SelectItem value="meet">Google Meet</SelectItem>
                    <SelectItem value="teams">Microsoft Teams</SelectItem>
                    <SelectItem value="webinar-jam">WebinarJam</SelectItem>
                    <SelectItem value="youtube-live">YouTube Live</SelectItem>
                    <SelectItem value="instagram-live">Instagram Live</SelectItem>
                    <SelectItem value="fisico">Evento Físico/Presencial</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Sala URLs - Only for Lanzamiento strategy */}
          {strategy === 'lanzamiento' && (
            <div className="space-y-4 mt-4 pt-4 border-t border-border-subtle/50">
              <div className="flex items-center gap-2 mb-2">
                <LinkIcon className="w-4 h-4 text-brand-500" />
                <span className="text-sm font-medium text-text-main">URLs de las Salas del Directo</span>
                <span className="text-xs text-text-muted">(opcional)</span>
              </div>
              
              {/* 1-directo: Single sala */}
              {variant === '1-directo' && (
                <div className="space-y-2">
                  <Label className="text-text-secondary flex items-center gap-2">
                    <Video className="w-4 h-4" />
                    URL de la sala del directo
                  </Label>
                  <Input
                    type="url"
                    value={salaDirecto1Url}
                    onChange={(e) => setSalaDirecto1Url(e.target.value)}
                    placeholder="https://zoom.us/j/... o cualquier plataforma"
                    className="bg-app-bg border-border-subtle text-text-main placeholder:text-text-muted focus:border-brand-500"
                  />
                </div>
              )}
              
              {/* 2-directos: Two salas */}
              {variant === '2-directos' && (
                <div className="space-y-4">
                  {/* Fecha y hora del Directo 2 */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-text-secondary flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        Fecha del Directo 2
                      </Label>
                      <Input
                        type="date"
                        value={fechaDirecto2}
                        onChange={(e) => setFechaDirecto2(e.target.value)}
                        className="bg-app-bg border-border-subtle text-text-main focus:border-brand-500"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-text-secondary flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        Hora del Directo 2
                      </Label>
                      <Input
                        type="time"
                        value={horaDirecto2}
                        onChange={(e) => setHoraDirecto2(e.target.value)}
                        className="bg-app-bg border-border-subtle text-text-main focus:border-brand-500"
                      />
                    </div>
                  </div>
                  
                  {/* URLs de las salas */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-text-secondary flex items-center gap-2">
                        <Video className="w-4 h-4" />
                        URL sala Directo 1 (Apertura)
                      </Label>
                      <Input
                        type="url"
                        value={salaDirecto1Url}
                        onChange={(e) => setSalaDirecto1Url(e.target.value)}
                        placeholder="https://zoom.us/j/..."
                        className="bg-app-bg border-border-subtle text-text-main placeholder:text-text-muted focus:border-brand-500"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-text-secondary flex items-center gap-2">
                        <Video className="w-4 h-4" />
                        URL sala Directo 2 (Ventas)
                      </Label>
                      <Input
                        type="url"
                        value={salaDirecto2Url}
                        onChange={(e) => setSalaDirecto2Url(e.target.value)}
                        placeholder="https://zoom.us/j/..."
                        className="bg-app-bg border-border-subtle text-text-main placeholder:text-text-muted focus:border-brand-500"
                      />
                    </div>
                  </div>
                </div>
              )}
              
              {/* 3-directos: Three salas */}
              {variant === '3-directos' && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-text-secondary flex items-center gap-2">
                      <Video className="w-4 h-4" />
                      URL sala Live 1
                    </Label>
                    <Input
                      type="url"
                      value={salaDirecto1Url}
                      onChange={(e) => setSalaDirecto1Url(e.target.value)}
                      placeholder="https://zoom.us/j/..."
                      className="bg-app-bg border-border-subtle text-text-main placeholder:text-text-muted focus:border-brand-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-text-secondary flex items-center gap-2">
                      <Video className="w-4 h-4" />
                      URL sala Live 2
                    </Label>
                    <Input
                      type="url"
                      value={salaDirecto2Url}
                      onChange={(e) => setSalaDirecto2Url(e.target.value)}
                      placeholder="https://zoom.us/j/..."
                      className="bg-app-bg border-border-subtle text-text-main placeholder:text-text-muted focus:border-brand-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-text-secondary flex items-center gap-2">
                      <Video className="w-4 h-4" />
                      URL sala Live 3 (Ventas)
                    </Label>
                    <Input
                      type="url"
                      value={salaDirecto3Url}
                      onChange={(e) => setSalaDirecto3Url(e.target.value)}
                      placeholder="https://zoom.us/j/..."
                      className="bg-app-bg border-border-subtle text-text-main placeholder:text-text-muted focus:border-brand-500"
                    />
                  </div>
                </div>
              )}
              
              {/* serie-lives: Three live URLs */}
              {variant === 'serie-lives' && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-text-secondary flex items-center gap-2">
                      <Video className="w-4 h-4" />
                      URL sala Live 1
                    </Label>
                    <Input
                      type="url"
                      value={salaLive1Url}
                      onChange={(e) => setSalaLive1Url(e.target.value)}
                      placeholder="https://youtube.com/live/..."
                      className="bg-app-bg border-border-subtle text-text-main placeholder:text-text-muted focus:border-brand-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-text-secondary flex items-center gap-2">
                      <Video className="w-4 h-4" />
                      URL sala Live 2
                    </Label>
                    <Input
                      type="url"
                      value={salaLive2Url}
                      onChange={(e) => setSalaLive2Url(e.target.value)}
                      placeholder="https://youtube.com/live/..."
                      className="bg-app-bg border-border-subtle text-text-main placeholder:text-text-muted focus:border-brand-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-text-secondary flex items-center gap-2">
                      <Video className="w-4 h-4" />
                      URL sala Live 3
                    </Label>
                    <Input
                      type="url"
                      value={salaLive3Url}
                      onChange={(e) => setSalaLive3Url(e.target.value)}
                      placeholder="https://youtube.com/live/..."
                      className="bg-app-bg border-border-subtle text-text-main placeholder:text-text-muted focus:border-brand-500"
                    />
                  </div>
                </div>
              )}
              
              <p className="text-xs text-text-muted mt-2">
                Si no rellenas estas URLs ahora, la landing mostrará [AÑADIR URL DE SALA] para que lo completes después.
              </p>
              
              {/* Replay URLs - Only show for variants that have replay */}
              {(variant === '1-directo' || variant === '2-directos' || variant === '3-directos') && (
                <div className="mt-4 pt-4 border-t border-border-subtle/30">
                  <div className="flex items-center gap-2 mb-3">
                    <Play className="w-4 h-4 text-green-500" />
                    <span className="text-sm font-medium text-text-main">URLs de Replay</span>
                    <span className="text-xs text-text-muted">(añadir después del evento)</span>
                  </div>
                  
                  <div className={`grid grid-cols-1 ${variant === '1-directo' ? '' : variant === '2-directos' ? 'md:grid-cols-2' : 'md:grid-cols-3'} gap-4`}>
                    <div className="space-y-2">
                      <Label className="text-text-secondary flex items-center gap-2">
                        <Play className="w-4 h-4" />
                        {variant === '1-directo' ? 'URL del Replay' : 'URL Replay Directo 1'}
                      </Label>
                      <Input
                        type="url"
                        value={replayDirecto1Url}
                        onChange={(e) => setReplayDirecto1Url(e.target.value)}
                        placeholder="https://youtube.com/watch?v=..."
                        className="bg-app-bg border-border-subtle text-text-main placeholder:text-text-muted focus:border-brand-500"
                      />
                    </div>
                    
                    {(variant === '2-directos' || variant === '3-directos') && (
                      <div className="space-y-2">
                        <Label className="text-text-secondary flex items-center gap-2">
                          <Play className="w-4 h-4" />
                          URL Replay Directo 2
                        </Label>
                        <Input
                          type="url"
                          value={replayDirecto2Url}
                          onChange={(e) => setReplayDirecto2Url(e.target.value)}
                          placeholder="https://youtube.com/watch?v=..."
                          className="bg-app-bg border-border-subtle text-text-main placeholder:text-text-muted focus:border-brand-500"
                        />
                      </div>
                    )}
                    
                    {variant === '3-directos' && (
                      <div className="space-y-2">
                        <Label className="text-text-secondary flex items-center gap-2">
                          <Play className="w-4 h-4" />
                          URL Replay Directo 3
                        </Label>
                        <Input
                          type="url"
                          value={replayDirecto3Url}
                          onChange={(e) => setReplayDirecto3Url(e.target.value)}
                          placeholder="https://youtube.com/watch?v=..."
                          className="bg-app-bg border-border-subtle text-text-main placeholder:text-text-muted focus:border-brand-500"
                        />
                      </div>
                    )}
                  </div>
                  
                  <p className="text-xs text-text-muted mt-2">
                    Cuando el evento termine, pega aquí la URL de la grabación y la Sala del Directo mostrará el replay automáticamente.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Urgencia, Garantía y Recursos - Only for Lanzamiento strategy */}
          {strategy === 'lanzamiento' && (
            <div className="space-y-4 mt-4 pt-4 border-t border-border-subtle/50">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-4 h-4 text-amber-500" />
                <span className="text-sm font-medium text-text-main">Urgencia, Garantía y Recursos</span>
                <span className="text-xs text-text-muted">(opcional)</span>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-text-secondary flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Duración oferta tras directo
                  </Label>
                  <Input
                    value={cuentaAtrasOferta}
                    onChange={(e) => setCuentaAtrasOferta(e.target.value)}
                    placeholder="48"
                    className="bg-app-bg border-border-subtle text-text-main placeholder:text-text-muted focus:border-brand-500"
                  />
                  <span className="text-xs text-text-muted">Horas hasta cierre de carrito</span>
                </div>
                <div className="space-y-2">
                  <Label className="text-text-secondary flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Días de garantía
                  </Label>
                  <Input
                    value={diasGarantia}
                    onChange={(e) => setDiasGarantia(e.target.value)}
                    placeholder="30"
                    className="bg-app-bg border-border-subtle text-text-main placeholder:text-text-muted focus:border-brand-500"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-text-secondary flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Plazas restantes
                  </Label>
                  <Input
                    value={plazasRestantes}
                    onChange={(e) => setPlazasRestantes(e.target.value)}
                    placeholder="50"
                    className="bg-app-bg border-border-subtle text-text-main placeholder:text-text-muted focus:border-brand-500"
                  />
                  <span className="text-xs text-text-muted">Al precio de lanzamiento</span>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-text-secondary flex items-center gap-2">
                    <MessageCircle className="w-4 h-4" />
                    URL grupo privado (WhatsApp/Telegram)
                  </Label>
                  <Input
                    type="url"
                    value={grupoPrivadoUrl}
                    onChange={(e) => setGrupoPrivadoUrl(e.target.value)}
                    placeholder="https://chat.whatsapp.com/..."
                    className="bg-app-bg border-border-subtle text-text-main placeholder:text-text-muted focus:border-brand-500"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-text-secondary flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    URL área de miembros
                  </Label>
                  <Input
                    type="url"
                    value={areaMiembrosUrl}
                    onChange={(e) => setAreaMiembrosUrl(e.target.value)}
                    placeholder="https://tusitio.com/area-miembros"
                    className="bg-app-bg border-border-subtle text-text-main placeholder:text-text-muted focus:border-brand-500"
                  />
                </div>
              </div>
            </div>
          )}

          {needsEcommerceFields && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-text-secondary flex items-center gap-2">
                  <Percent className="w-4 h-4" />
                  Porcentaje de Descuento / Oferta
                </Label>
                <Input
                  value={discountPercent}
                  onChange={(e) => setDiscountPercent(e.target.value)}
                  placeholder="Ej: 30% o 2x1 o Envío gratis"
                  className="bg-app-bg border-border-subtle text-text-main placeholder:text-text-muted focus:border-brand-500"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-text-secondary flex items-center gap-2">
                  <Truck className="w-4 h-4" />
                  Condiciones de Envío
                </Label>
                <Input
                  value={shippingConditions}
                  onChange={(e) => setShippingConditions(e.target.value)}
                  placeholder="Ej: Envío gratis +50€, 24-48h península"
                  className="bg-app-bg border-border-subtle text-text-main placeholder:text-text-muted focus:border-brand-500"
                />
              </div>
            </div>
          )}

          {needsSaaSFields && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-text-secondary flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  Características Core del Software
                </Label>
                <Textarea
                  value={coreFeatures}
                  onChange={(e) => setCoreFeatures(e.target.value)}
                  placeholder="Dashboard analítico, integraciones con CRM, reportes automatizados..."
                  rows={2}
                  className="bg-app-bg border-border-subtle text-text-main placeholder:text-text-muted focus:border-brand-500 resize-none"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-text-secondary flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  Alcance de Configuración
                </Label>
                <Select value={configScope} onValueChange={setConfigScope}>
                  <SelectTrigger className="bg-app-bg border-border-subtle text-text-main">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-surface border-border-subtle">
                    <SelectItem value="global">Global - Cambios afectan a todos los usuarios</SelectItem>
                    <SelectItem value="account">Por Cuenta - Cada cuenta tiene su configuración</SelectItem>
                    <SelectItem value="store">Por Tienda - Cada tienda/sucursal individual</SelectItem>
                    <SelectItem value="user">Por Usuario - Configuración personal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {needsLocalFields && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-text-secondary flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Dirección Física
                </Label>
                <Input
                  value={physicalAddress}
                  onChange={(e) => setPhysicalAddress(e.target.value)}
                  placeholder="Calle Principal 123, Madrid"
                  className="bg-app-bg border-border-subtle text-text-main placeholder:text-text-muted focus:border-brand-500"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-text-secondary flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  Radio de Acción / Ciudad
                </Label>
                <Input
                  value={serviceRadius}
                  onChange={(e) => setServiceRadius(e.target.value)}
                  placeholder="Ej: Madrid centro, 15km alrededor"
                  className="bg-app-bg border-border-subtle text-text-main placeholder:text-text-muted focus:border-brand-500"
                />
              </div>
            </div>
          )}

          {needsHighTicketFields && (
            <div className="space-y-2">
              <Label className="text-text-secondary flex items-center gap-2">
                <Award className="w-4 h-4" />
                Requisito Mínimo para Agendar
              </Label>
              <Input
                value={minimumRequirement}
                onChange={(e) => setMinimumRequirement(e.target.value)}
                placeholder="Ej: Facturación mínima 100K€/año, presupuesto de 5K€"
                className="bg-app-bg border-border-subtle text-text-main placeholder:text-text-muted focus:border-brand-500"
              />
            </div>
          )}

          {needsIAFields && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-text-secondary flex items-center gap-2">
                  <Bot className="w-4 h-4" />
                  Plataforma del Bot
                </Label>
                <Select value={botPlatform} onValueChange={setBotPlatform}>
                  <SelectTrigger className="bg-app-bg border-border-subtle text-text-main">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-surface border-border-subtle">
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="web">Chatbot Web</SelectItem>
                    <SelectItem value="voice">Llamada de Voz IA</SelectItem>
                    <SelectItem value="instagram">Instagram DM</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-text-secondary flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Tono del Asistente
                </Label>
                <Select value={botTone} onValueChange={setBotTone}>
                  <SelectTrigger className="bg-app-bg border-border-subtle text-text-main">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-surface border-border-subtle">
                    <SelectItem value="profesional">Profesional y formal</SelectItem>
                    <SelectItem value="amigable">Amigable y cercano</SelectItem>
                    <SelectItem value="entusiasta">Entusiasta y enérgico</SelectItem>
                    <SelectItem value="empático">Empático y comprensivo</SelectItem>
                    <SelectItem value="directo">Directo y conciso</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ==================== BRAND ASSETS ==================== */}
      <BrandAssets
        assets={brandAssets}
        onAssetsChange={setBrandAssets}
        isExpanded={false}
      />

      {/* ==================== GENERATE BUTTON ==================== */}
      <div className="bg-gradient-to-r from-brand/10 to-purple-600/10 border border-brand/30 rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-brand/20 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-brand-500" />
            </div>
            <div>
              <h3 className="font-semibold text-text-main">Magia IA</h3>
              <p className="text-sm text-text-muted">
                Genera avatar, propuesta de valor, oferta y titulares
              </p>
            </div>
          </div>
          
          <Button
            onClick={() => {
              if (!isFormValid) {
                setShowValidationErrors(true);
                // Scroll to first error
                const firstError = document.querySelector('.border-red-500');
                if (firstError) {
                  firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
                return;
              }
              generateBriefWithAI();
            }}
            className={`px-6 py-3 h-auto transition-all ${
              isFormValid
                ? 'bg-gradient-to-r from-brand to-purple-600 hover:from-brand-hover hover:to-purple-700 text-white'
                : 'bg-gray-600 text-gray-400 cursor-pointer hover:bg-gray-500'
            }`}
            data-testid="generate-brief-btn"
          >
            <Sparkles className="w-5 h-5 mr-2" />
            Generar Brief de Marketing
          </Button>
        </div>
        
        {!isFormValid && (
          <p className="text-amber-400 text-sm mt-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            {isProductNameEmpty && needsEventFields && isExpertNameEmpty 
              ? 'Falta: Nombre del producto y Nombre del experto (marcados en rojo arriba)'
              : isProductNameEmpty 
                ? 'Falta: Nombre del producto (marcado en rojo arriba)'
                : needsEventFields && isExpertNameEmpty
                  ? 'Falta: Nombre del experto (marcado en rojo arriba)'
                  : 'Completa los campos requeridos'
            }
          </p>
        )}
      </div>
      
      {/* ==================== OVERWRITE CONFIRMATION MODAL ==================== */}
      {showOverwriteConfirm && (
        <div 
          className="fixed inset-0 z-[9999] flex items-start justify-center pt-20 bg-black/50 backdrop-blur-sm overflow-y-auto"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowOverwriteConfirm(false);
          }}
        >
          <div className="bg-surface border border-border-subtle rounded-2xl p-6 max-w-md mx-4 shadow-xl my-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-text-main">Sobrescribir datos</h3>
                <p className="text-sm text-text-muted">Ya tienes información en el formulario</p>
              </div>
            </div>
            
            <p className="text-text-secondary mb-6">
              Al analizar la web, se sobrescribirán los campos actuales con la información extraída. 
              ¿Deseas continuar?
            </p>
            
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowOverwriteConfirm(false)}
                className="border-border-subtle text-text-secondary"
              >
                Cancelar
              </Button>
              <Button
                onClick={() => executeWebAnalysis()}
                className="bg-brand-500 hover:bg-brand-hover text-white"
              >
                <Search className="w-4 h-4 mr-2" />
                Sí, analizar web
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BriefStep;
